"use client";

import { Fragment, useState, useEffect, useLayoutEffect, useRef, useCallback, useMemo, type CSSProperties, type FC, type ReactNode } from "react";
import { createPortal } from "react-dom";
import {  XIcon, SendIcon, Maximize2, Minimize2, ThumbsUp, ThumbsDown } from "lucide-react";
import type { PlatformResponse, AdMeshTheme, ChatMessage } from "../types/index";
import { AdMeshRecommendations } from "./AdMeshRecommendations";
import { useAdMesh } from "../hooks/useAdMesh";
import { useAdMeshStyles } from "../hooks/useAdMeshStyles";
import { useAdMeshContext } from "../context/AdMeshContext";
import type { PublisherAssistantAnalyticsEvent } from "../sdk/AdMeshSDK";

function formatGenerationDuration(totalSeconds: number): string {
  const seconds = Math.max(1, Math.round(totalSeconds));
  if (seconds < 60) {
    return `${seconds}s`;
  }
  const minutes = Math.floor(seconds / 60);
  const remainder = seconds % 60;
  return remainder > 0 ? `${minutes}m ${remainder}s` : `${minutes}m`;
}

function formatGenerationDurationFromMs(durationMs: number): string {
  return formatGenerationDuration(durationMs / 1000);
}

type AnswerFeedbackValue = 'helpful' | 'not_helpful';

// Custom markdown renderer with proper styling
const CustomMarkdownRenderer = ({ content, theme, fontSize }: { content: string; theme: AdMeshTheme; fontSize: string }) => {
  const renderInlineFormatting = (text: string) => {
    const parts = text.split(/(\*\*[^*]+\*\*|\[[^\]]+\]\(https?:\/\/[^)\s]+\))/g);
    return parts.map((part, index) => {
      if (part.startsWith('**') && part.endsWith('**') && part.length > 4) {
        return (
          <strong key={index} style={{ fontWeight: '600', color: theme.textColor }}>
            {part.slice(2, -2)}
          </strong>
        );
      }
      const linkMatch = part.match(/^\[([^\]]+)\]\((https?:\/\/[^)\s]+)\)$/);
      if (linkMatch) {
        return (
          <a
            key={index}
            href={linkMatch[2]}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: theme.primaryColor, textDecoration: 'underline', textUnderlineOffset: '2px' }}
          >
            {linkMatch[1]}
          </a>
        );
      }
      return <Fragment key={index}>{part}</Fragment>;
    });
  };

  const parseTableRow = (line: string) => {
    const trimmed = line.trim().replace(/^\|/, '').replace(/\|$/, '');
    return trimmed.split('|').map((cell) => cell.trim()).filter((cell, index, cells) => (
      cell.length > 0 || index < cells.length - 1
    ));
  };

  const isTableSeparator = (line: string) => {
    const cells = parseTableRow(line);
    return cells.length > 1 && cells.every((cell) => /^:?-{3,}:?$/.test(cell));
  };

  const splitTablePrefixHeading = (line: string) => {
    const pipeIndex = line.indexOf('|');
    if (pipeIndex <= 0) {
      return { heading: null as string | null, headerLine: line };
    }
    const prefix = line.slice(0, pipeIndex).trim();
    if (!prefix || /^[-|:\s]+$/.test(prefix)) {
      return { heading: null, headerLine: line };
    }
    return { heading: prefix, headerLine: line.slice(pipeIndex).trim() };
  };

  const normalizeTableSeparator = (headerLine: string, separatorLine: string) => {
    const headerCount = parseTableRow(headerLine).length;
    const separatorCount = parseTableRow(separatorLine).length;
    if (headerCount <= 1 || headerCount === separatorCount) {
      return separatorLine;
    }
    return `| ${Array.from({ length: headerCount }, () => '---').join(' | ')} |`;
  };

  const normalizeTableLines = (lines: string[]) => {
    if (lines.length < 2 || !lines[0].includes('|')) {
      return { heading: null as string | null, lines };
    }
    const { heading, headerLine } = splitTablePrefixHeading(lines[0]);
    const normalizedHeader = headerLine.startsWith('|') ? headerLine : `| ${headerLine}`;
    const normalizedSeparator = normalizeTableSeparator(normalizedHeader, lines[1]);
    return {
      heading,
      lines: [normalizedHeader, normalizedSeparator, ...lines.slice(2)],
    };
  };

  const isMarkdownTable = (lines: string[]) => {
    if (lines.length < 2 || !lines[0].includes('|')) {
      return false;
    }
    const { lines: normalizedLines } = normalizeTableLines(lines);
    return (
      isTableSeparator(normalizedLines[1]) &&
      parseTableRow(normalizedLines[0]).length >= 2
    );
  };

  const expandInlineTableLine = (line: string) => {
    if (!line.includes('|')) {
      return [line];
    }

    let working = line.trim();
    const result: string[] = [];

    const pipeIndex = working.indexOf('|');
    if (pipeIndex > 0) {
      const prefix = working.slice(0, pipeIndex).trim();
      if (prefix && !/^[-|:\s]+$/.test(prefix)) {
        result.push(prefix);
        working = working.slice(pipeIndex).trim();
      }
    }

    // Some models (e.g. Gemma) emit header + separator + rows on one line.
    working = working.replace(/\|\s*(\|(?:\s*:?-{3,}:?\s*\|)+)/g, (_match, separatorRow: string) => `\n${separatorRow}`);
    working = working.replace(/\|\s*\|\s*(?=[A-Za-z0-9*])/g, '|\n| ');
    working = working.replace(/\|\s+(?=\|(?!\s*:?-{3,}:?\s*\|)[^|\n])/g, '|\n');

    result.push(...working.split('\n').map((part) => part.trim()).filter(Boolean));
    return result;
  };

  const normalizeCompactMarkdownTables = (markdown: string) => {
    const lines = markdown.split('\n');
    const normalized: string[] = [];

    for (const line of lines) {
      if (!line.includes('|')) {
        normalized.push(line);
        continue;
      }
      normalized.push(...expandInlineTableLine(line));
    }

    return normalized.join('\n');
  };

  const isolateMarkdownTables = (markdown: string) => {
    const lines = normalizeCompactMarkdownTables(markdown).split('\n');
    const output: string[] = [];
    let index = 0;

    while (index < lines.length) {
      const line = lines[index];
      const nextLine = lines[index + 1] || '';

      if (line.includes('|') && isTableSeparator(nextLine)) {
        if (output.length > 0 && output[output.length - 1].trim()) {
          output.push('');
        }

        const { heading, headerLine } = splitTablePrefixHeading(line);
        if (heading) {
          output.push(heading);
        }
        output.push(headerLine, normalizeTableSeparator(headerLine, nextLine));
        index += 2;

        while (index < lines.length && lines[index].includes('|') && lines[index].trim()) {
          output.push(lines[index]);
          index += 1;
        }

        if (index < lines.length && lines[index].trim()) {
          output.push('');
        }
        continue;
      }

      output.push(line);
      index += 1;
    }

    return output.join('\n');
  };

  const blocks = isolateMarkdownTables(content)
    .split(/\n\s*\n/)
    .map((block) => block.trim())
    .filter(Boolean);

  return (
    <div style={{ margin: 0, color: theme.textColor, fontSize }}>
      {blocks.map((block, blockIndex) => {
        const lines = block.split('\n').map((line) => line.trim()).filter(Boolean);
        const isBulletList = lines.every((line) => /^[-*]\s+/.test(line));
        const isNumberedList = lines.every((line) => /^\d+\.\s+/.test(line));
        const isHeading = lines.length === 1 && /^#{1,3}\s+/.test(lines[0]);
        const isTable = isMarkdownTable(lines);

        if (isHeading) {
          const text = lines[0].replace(/^#{1,3}\s+/, '');
          return (
            <h3
              key={blockIndex}
              style={{
                marginTop: blockIndex === 0 ? 0 : '1rem',
                marginBottom: '0.75rem',
                fontWeight: '600',
                fontSize: '1.05em',
                color: theme.textColor,
              }}
            >
              {renderInlineFormatting(text)}
            </h3>
          );
        }

        if (isTable) {
          const { heading, lines: tableLines } = normalizeTableLines(lines);
          const headers = parseTableRow(tableLines[0]);
          const rows = tableLines.slice(2).map(parseTableRow).filter((row) => row.length === headers.length);

          return (
            <div
              key={blockIndex}
              style={{
                width: '100%',
                overflowX: 'auto',
                marginBottom: '1rem',
              }}
            >
              {heading ? (
                <p
                  style={{
                    marginTop: blockIndex === 0 ? 0 : '1rem',
                    marginBottom: '0.75rem',
                    fontWeight: 600,
                    color: theme.textColor,
                    fontSize,
                  }}
                >
                  {renderInlineFormatting(heading)}
                </p>
              ) : null}
              <div
                style={{
                  border: `1px solid ${theme.borderColor || 'rgba(148, 163, 184, 0.28)'}`,
                  borderRadius: 8,
                  overflowX: 'auto',
                }}
              >
              <table
                style={{
                  width: '100%',
                  minWidth: headers.length > 3 ? 520 : undefined,
                  borderCollapse: 'collapse',
                  color: theme.textColor,
                  fontSize,
                  lineHeight: 1.45,
                }}
              >
                <thead>
                  <tr>
                    {headers.map((header, index) => (
                      <th
                        key={index}
                        style={{
                          textAlign: 'left',
                          padding: '9px 10px',
                          fontWeight: 600,
                          background: theme.surfaceColor || 'rgba(148, 163, 184, 0.12)',
                          borderBottom: `1px solid ${theme.borderColor || 'rgba(148, 163, 184, 0.28)'}`,
                          whiteSpace: 'normal',
                        }}
                      >
                        {renderInlineFormatting(header)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, rowIndex) => (
                    <tr key={rowIndex}>
                      {row.map((cell, cellIndex) => (
                        <td
                          key={cellIndex}
                          style={{
                            padding: '9px 10px',
                            verticalAlign: 'top',
                            borderBottom:
                              rowIndex === rows.length - 1
                                ? 'none'
                                : `1px solid ${theme.borderColor || 'rgba(148, 163, 184, 0.18)'}`,
                            whiteSpace: 'normal',
                          }}
                        >
                          {renderInlineFormatting(cell)}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            </div>
          );
        }

        if (isBulletList || isNumberedList) {
          const ListTag = isNumberedList ? 'ol' : 'ul';
          return (
            <ListTag
              key={blockIndex}
              style={{
                marginBottom: '1rem',
                paddingLeft: '1.25rem',
                color: theme.textColor,
                fontSize,
              }}
            >
              {lines.map((line, lineIndex) => (
                <li key={lineIndex} style={{ marginBottom: '0.45rem' }}>
                  {renderInlineFormatting(line.replace(/^([-*]|\d+\.)\s+/, ''))}
                </li>
              ))}
            </ListTag>
          );
        }

        return (
          <p
            key={blockIndex}
            style={{
              marginBottom: '1rem',
              lineHeight: '1.6',
              color: theme.textColor,
              fontSize,
              whiteSpace: 'pre-wrap',
            }}
          >
            {lines.map((line, lineIndex) => (
              <Fragment key={lineIndex}>
                {lineIndex > 0 && <br />}
                {renderInlineFormatting(line)}
              </Fragment>
            ))}
          </p>
        );
      })}
    </div>
  );
};

// Types for the Intent Assistant
export interface Suggestion {
  id: string;
  text: string;
  type: 'product' | 'comparison' | 'information' | 'action';
  is_sponsored?: boolean;
  recommendation?: PlatformResponse;
  metadata?: Record<string, unknown>;
}

export interface AssistantSourceLink {
  title?: string;
  url: string;
}

export interface AssistantRelatedItem {
  title?: string;
  url: string;
  snippet?: string;
  image_url?: string | null;
  publisher?: string | null;
}

export interface AssistantQueryResult {
  answer: string;
  source_links?: AssistantSourceLink[];
  related_items?: AssistantRelatedItem[];
  recommendation?: PlatformResponse | null;
  followup_suggestions?: Array<{ text: string; type?: Suggestion['type'] }>;
}

export interface AssistantQueryContext {
  sponsoredSuggestion?: Suggestion | null;
}

type SuggestionStyle = 'carousel' | 'stacked';

function toCssSize(value?: number | string) {
  return value === undefined ? undefined : typeof value === 'number' ? `${value}px` : value;
}

function toNumericCssSize(value?: number | string) {
  if (typeof value === 'number') return value;
  if (typeof value !== 'string') return undefined;
  const numeric = Number(value.trim().replace(/px$/i, ''));
  return Number.isFinite(numeric) && numeric > 0 ? numeric : undefined;
}

export type AdSlotBehavior = 'sticky' | 'expand';

export interface AdMeshIntentAssistantProps {
  // AssistantModal Root props
  defaultOpen?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  unstable_openOnRunStart?: boolean;
  
  // UI Configuration
  layout?: 'floating' | 'inline';
  surface?: 'page' | 'ad-slot';
  /** Ad-slot only: `expand` (default) opens a full-page overlay when active; `sticky` keeps the assistant inside the slot. */
  adSlotBehavior?: AdSlotBehavior;
  /** Ad-slot compact teaser: show header row (off for short formats like 300×250, 728×90). */
  showCompactSlotHeader?: boolean;
  /** Ad-slot compact teaser: show welcome title (off for very short formats like 728×90). */
  showCompactWelcomeMessage?: boolean;
  /** Ad-slot compact teaser: show input + send row (off for very short formats like 728×90). */
  showCompactSlotInput?: boolean;
  /** Ad-slot compact teaser: show AI disclaimer (off for very short formats like 728×90). */
  showCompactSlotDisclaimer?: boolean;
  position?: 'bottom-right' | 'bottom-left' | 'top-right' | 'top-left';
  size?: 'sm' | 'md' | 'lg' | 'xl';
  theme?: AdMeshTheme;
  className?: string;
  style?: CSSProperties;
  triggerLabel?: string;
  triggerIcon?: ReactNode;
  headerTitle?: string;
  headerIcon?: ReactNode;
  headerLogoUrl?: string | null;
  showHeader?: boolean;
  showHeaderTitle?: boolean;
  showSubtitle?: boolean;
  panelWidth?: number | string;
  panelHeight?: number | string;
  fontScale?: number;
  analyticsPageUrl?: string;
  
  // Behavior
  autoOpen?: boolean;
  showWelcomeMessage?: boolean;
  welcomeMessage?: string;
  placeholder?: string;
  adsEnabled?: boolean;
  suggestions?: Suggestion[]; // Optional - will use context suggestions if not provided
  maxSuggestions?: number;
  suggestionStyle?: SuggestionStyle;
  onlyShowWhenSponsored?: boolean; // Only show modal when sponsored suggestions are available
  variant?: 'default' | 'legacy';
  queryHandler?: (query: string, context: AssistantQueryContext) => Promise<AssistantQueryResult>;
  suggestionsLoader?: () => Promise<Suggestion[]>;
  disclaimerText?: string;
  poweredByLabel?: string;
  subtitle?: string;
  
  // Events
  onOpen?: () => void;
  onClose?: () => void;
}

function normalizeSuggestionText(value: string): string {
  return value.replace(/\s+/g, ' ').trim();
}

function mergeSuggestionsPreservingOrganic(
  currentSuggestions: Suggestion[],
  nextSuggestions: Suggestion[],
): Suggestion[] {
  const currentOrganicSuggestions = currentSuggestions
    .filter((suggestion) => !suggestion?.is_sponsored)
    .slice(0, 3);
  const nextOrganicSuggestions = nextSuggestions
    .filter((suggestion) => !suggestion?.is_sponsored)
    .slice(0, 3);

  const sponsoredSuggestion = nextSuggestions.find((suggestion) => suggestion?.is_sponsored);

  if (!sponsoredSuggestion) {
    return nextOrganicSuggestions.length ? nextOrganicSuggestions : currentOrganicSuggestions;
  }

  const organicSuggestions = nextOrganicSuggestions.length ? nextOrganicSuggestions : currentOrganicSuggestions;
  return [sponsoredSuggestion, ...organicSuggestions].slice(0, 4);
}

function buildFollowupSuggestionsFromAnswer(
  query: string,
  answer: string,
  sourceLinks?: AssistantSourceLink[],
  relatedItems?: AssistantRelatedItem[],
): Suggestion[] {
  const seen = new Set<string>();
  const suggestions: Suggestion[] = [];

  const pushSuggestion = (text: string, type: Suggestion['type'] = 'information') => {
    const normalized = normalizeSuggestionText(text);
    if (!normalized) return;
    const key = normalized.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    suggestions.push({
      id: `followup_${suggestions.length}_${key.replace(/[^a-z0-9]+/g, '_')}`,
      text: normalized,
      type,
      is_sponsored: false,
    });
  };

  const topics = new Set<string>();

  const collectTopic = (value?: string | null) => {
    const normalized = normalizeSuggestionText(value || '');
    if (!normalized) return;
    const cleaned = normalized
      .replace(/^(how|why|what|when|where|which|who)\s+/i, '')
      .replace(/\?+$/g, '')
      .trim();
    if (!cleaned) return;
    topics.add(cleaned);
  };

  (relatedItems || []).forEach((item) => collectTopic(item?.title));
  (sourceLinks || []).forEach((item) => collectTopic(item?.title));

  const sentenceMatches = answer
    .split(/\n+/)
    .map((line) => normalizeSuggestionText(line.replace(/^[-*]\s+/, '')))
    .filter(Boolean)
    .filter((line) => line.length >= 24 && line.length <= 90);

  sentenceMatches.forEach((line) => {
    if (topics.size >= 6) return;
    collectTopic(line);
  });

  const cleanQuery = normalizeSuggestionText(query.replace(/[?.!]+$/g, ''));
  const topicList = Array.from(topics).slice(0, 3);
  const continuations = [
    topicList[0] ? `Tell me more about ${topicList[0]}` : '',
    topicList[1] ? `Show me examples related to ${topicList[1]}` : '',
    topicList[2] ? `Compare the main options for ${topicList[2]}` : '',
    `Break this down more simply`,
    `What should I look at next about ${cleanQuery}`,
    `Give me the practical takeaway here`,
  ];

  continuations.forEach((text, index) => {
    if (suggestions.length >= 3) return;
    pushSuggestion(text, index === 0 ? 'comparison' : 'information');
  });

  return suggestions.slice(0, 3);
}

function detectAssistantDeviceType(): PublisherAssistantAnalyticsEvent['device_type'] {
  if (typeof window === 'undefined') return 'unknown';
  const width = window.innerWidth;
  if (width < 768) return 'mobile';
  if (width < 1024) return 'tablet';
  return 'desktop';
}

type ExpandedOverlayLayout = {
  shell: CSSProperties;
  panelWidth: string;
  panelMaxWidth: string;
  panelHeight: string;
  panelMaxHeight?: string;
  panelBorderRadius: string;
};

function getExpandedOverlayLayout(isMobile: boolean): ExpandedOverlayLayout {
  if (isMobile) {
    return {
      shell: {
        position: 'fixed',
        left: 0,
        right: 0,
        bottom: 0,
        width: '100%',
        maxWidth: '100%',
        height: 'calc(100dvh - env(safe-area-inset-bottom, 0px))',
        maxHeight: 'calc(100dvh - env(safe-area-inset-bottom, 0px))',
        padding: 0,
        margin: 0,
        zIndex: 2147483001,
        boxSizing: 'border-box',
      },
      panelWidth: '100%',
      panelMaxWidth: '100%',
      panelHeight: '100%',
      panelMaxHeight: '100%',
      panelBorderRadius: '0',
    };
  }

  return {
    shell: {
      position: 'fixed',
      left: '50%',
      bottom: '12px',
      transform: 'translateX(-50%)',
      width: 'min(960px, calc(100vw - 24px))',
      maxWidth: '100%',
      maxHeight: 'calc(100vh - 24px)',
      zIndex: 2147483001,
      boxSizing: 'border-box',
    },
    panelWidth: 'min(960px, calc(100vw - 24px))',
    panelMaxWidth: '100%',
    panelHeight: 'min(78vh, 760px)',
    panelMaxHeight: undefined,
    panelBorderRadius: '18px',
  };
}

function isResolvedPageUrl(value?: string) {
  const trimmed = value?.trim();
  if (!trimmed || /%%[^%]+%%/.test(trimmed)) {
    return false;
  }
  try {
    const parsed = new URL(trimmed);
    return Boolean(parsed.protocol && parsed.hostname);
  } catch {
    return false;
  }
}

function getUsableWindowHref() {
  try {
    const href = window.location.href?.trim();
    if (isResolvedPageUrl(href)) {
      return href;
    }
  } catch {
    return undefined;
  }
  return undefined;
}

function getAssistantPageContext(pageUrlOverride?: string) {
  if (typeof window === 'undefined') {
    return {
      page_url: undefined,
      page_path: undefined,
      article_slug: undefined,
      page_title: undefined,
      referrer: undefined,
      device_type: 'unknown' as const,
    };
  }

  const resolvedPageUrl = getUsableWindowHref()
    || (isResolvedPageUrl(pageUrlOverride) ? pageUrlOverride!.trim() : undefined);
  let page_path: string | undefined = resolvedPageUrl ? window.location.pathname || '/' : undefined;
  try {
    page_path = resolvedPageUrl ? new URL(resolvedPageUrl).pathname || '/' : undefined;
  } catch {
    page_path = getUsableWindowHref() ? window.location.pathname || '/' : undefined;
  }
  const segments = (page_path || '').split('/').filter(Boolean);

  return {
    page_url: resolvedPageUrl,
    page_path,
    article_slug: segments[segments.length - 1],
    page_title: document.title || undefined,
    referrer: document.referrer || undefined,
    device_type: detectAssistantDeviceType(),
  };
}

function buildSponsoredSuggestionFromRecommendation(recommendation?: PlatformResponse | null): Suggestion | null {
  const suggestionText = recommendation?.recommendation?.creative?.follow_up_suggestion;
  const recommendationId = recommendation?.recommendation_id;
  if (!suggestionText || !recommendationId) {
    return null;
  }

  return {
    id: recommendationId,
    text: normalizeSuggestionText(suggestionText),
    type: 'action',
    is_sponsored: true,
    recommendation,
    metadata: {
      source: 'assistant_query_recommendation',
      recommendation_id: recommendationId,
    },
  };
}

function prioritizeSponsoredSuggestions(items: Suggestion[]): Suggestion[] {
  return [...items].sort((left, right) => {
    const leftSponsored = Boolean(left.is_sponsored);
    const rightSponsored = Boolean(right.is_sponsored);
    if (leftSponsored === rightSponsored) {
      return 0;
    }
    return leftSponsored ? -1 : 1;
  });
}

// Default theme
const defaultTheme: AdMeshTheme = {
  mode: 'light',
  primaryColor: '#3b82f6',
  secondaryColor: '#64748b',
  backgroundColor: '#ffffff',
  surfaceColor: '#f8fafc',
  borderColor: '#e2e8f0',
  textColor: '#1e293b',
  textSecondaryColor: '#64748b',
  borderRadius: '0.5rem',
  spacing: {
    small: '0.5rem',
    medium: '1rem',
    large: '1.5rem',
  },
  shadows: {
    small: '0 1px 2px 0 rgb(0 0 0 / 0.05)',
    medium: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
    large: '0 10px 15px -3px rgb(0 0 0 / 0.1)',
  },
};

// Size configurations
const sizeConfig = {
  sm: { width: '320px', height: '400px' },
  md: { width: '400px', height: '500px' },
  lg: { width: '480px', height: '600px' },
  xl: { width: '560px', height: '700px' },
};

// Position configurations
const positionConfig = {
  'bottom-right': { bottom: '20px', right: '20px' },
  'bottom-left': { bottom: '20px', left: '20px' },
  'top-right': { top: '20px', right: '20px' },
  'top-left': { top: '20px', left: '20px' },
};

// Main Assistant Component
export const AdMeshIntentAssistant: FC<AdMeshIntentAssistantProps> = ({
  theme: propTheme,
  className,
  style,
  triggerLabel = 'Ask Ava',
  headerTitle = 'Ask Ava',
  headerLogoUrl,
  showHeader = true,
  showHeaderTitle = true,
  showSubtitle = true,
  layout = 'floating',
  surface = 'page',
  adSlotBehavior = 'expand',
  showCompactSlotHeader = true,
  showCompactWelcomeMessage = true,
  showCompactSlotInput = true,
  showCompactSlotDisclaimer = true,
  panelWidth,
  panelHeight,
  analyticsPageUrl,
  fontScale = 1,
  position = 'bottom-right',
  size = 'md',
  // AssistantModal Root props
  defaultOpen,
  open,
  onOpenChange,
  // Behavior props
  autoOpen = false,
  showWelcomeMessage = false,
  welcomeMessage = "Hi, I’m Ava. I’ll help you find the best options.",
  placeholder = "Ask me anything about products...",
  adsEnabled = true,
  suggestions: propSuggestions, // Renamed to avoid conflict
  maxSuggestions = 6,
  suggestionStyle = 'carousel',
  onlyShowWhenSponsored = false, // New prop to only show modal when sponsored suggestions are available
  variant = 'default',
  queryHandler,
  suggestionsLoader,
  disclaimerText,
  poweredByLabel = 'Powered by AdMesh',
  subtitle = 'Publisher Assistant',
  onOpen,
  onClose,
}) => {
  // Get AdMesh context
  const { sdk, sessionId, theme: contextTheme, apiKey, userId } = useAdMesh();
  const { 
    suggestions: contextSuggestions, 
    isAnalyzingIntent, 
    intentError,
    retryIntentAnalysis,
    activeDelegationSession,
    hasActiveDelegationSession,
    stopDelegationSession,
    refreshDelegationActivity,
  } = useAdMeshContext();
  useAdMeshStyles();
  
  // Debug: Log context availability
  console.log('[AdMeshIntentAssistant] Context:', { 
    sdk:sdk, 
    sessionId, 
    hasContextTheme: !!contextTheme 
  });
  
  // Use prop theme or fallback to context theme or default theme
  const theme = propTheme || contextTheme || defaultTheme;
  const themeMode = theme?.mode || 'light';
  const isAdSlotSurface = surface === 'ad-slot';
  const isAdSlotCompactLayout = (forOverlay = false) =>
    isAdSlotSurface && !(forOverlay && adSlotBehavior === 'expand');
  // Use controlled or uncontrolled state based on props
  const [internalOpen, setInternalOpen] = useState(defaultOpen || autoOpen);
  const isControlled = open !== undefined;
  const isOpen = isControlled ? open : internalOpen;
  
  const [inputValue, setInputValue] = useState('');
  type RenderMessage = ChatMessage & {
    sourceLinks?: AssistantSourceLink[];
    relatedItems?: AssistantRelatedItem[];
    initialRecommendation?: PlatformResponse | null;
    responseTimeMs?: number;
  };
  const [messages, setMessages] = useState<RenderMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [sponsoredClickedMessages, setSponsoredClickedMessages] = useState<Set<string>>(new Set());
  const [resolvedSuggestions, setResolvedSuggestions] = useState<Suggestion[]>(propSuggestions || contextSuggestions || []);
  const [loadingLabelIndex, setLoadingLabelIndex] = useState(0);
  const [generationElapsedSec, setGenerationElapsedSec] = useState(0);
  const [messageFeedback, setMessageFeedback] = useState<Record<string, AnswerFeedbackValue>>({});
  const generationStartedAtRef = useRef<number | null>(null);
  const currentMessageIsSponsored = useRef(false);
  const currentSponsoredSuggestionRef = useRef<Suggestion | null>(null);
  const suggestionButtonRefs = useRef<Map<string, HTMLButtonElement>>(new Map());
  const suggestionExposureCleanupRef = useRef<Array<() => void>>([]);
  const messagesScrollRef = useRef<HTMLDivElement>(null);
  const activeSubmittedMessageRef = useRef<HTMLDivElement>(null);
  const activeSubmittedMessageIdRef = useRef<string | null>(null);
  const shouldAnchorSubmittedMessageRef = useRef(false);
  const suppressAutoScrollToBottomRef = useRef(false);
  const adSlotRootRef = useRef<HTMLDivElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const suggestionsScrollRef = useRef<HTMLDivElement>(null);
  const generationAbortRef = useRef<AbortController | null>(null);
  const activeAssistantMessageIdRef = useRef<string | null>(null);
  const [canScrollSuggestionsLeft, setCanScrollSuggestionsLeft] = useState(false);
  const [canScrollSuggestionsRight, setCanScrollSuggestionsRight] = useState(false);
  const canUseDirectAdMeshFetch = Boolean(apiKey?.toLowerCase().startsWith('pub_'));
  const connectedDelegationSession =
    hasActiveDelegationSession() && activeDelegationSession?.session_id === sessionId
      ? activeDelegationSession
      : null;
  const trackedSuggestionExposureKeysRef = useRef<Set<string>>(new Set());
  const trackedRecommendationKeysRef = useRef<Set<string>>(new Set());
  const loadedTrackedRef = useRef(false);
  const isAdSlotOverlayActive =
    isAdSlotSurface &&
    adSlotBehavior === 'expand' &&
    (isExpanded || isLoading);
  const [isMobileViewport, setIsMobileViewport] = useState(false);
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mediaQuery = window.matchMedia('(max-width: 767px)');
    const updateViewport = () => setIsMobileViewport(mediaQuery.matches);
    updateViewport();
    mediaQuery.addEventListener('change', updateViewport);
    return () => mediaQuery.removeEventListener('change', updateViewport);
  }, []);
  useEffect(() => {
    if (!isAdSlotOverlayActive || typeof document === 'undefined') return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isAdSlotOverlayActive]);
  const shouldExpandAdSlot =
    isAdSlotSurface &&
    adSlotBehavior === 'sticky' &&
    (isExpanded || messages.length > 0 || isLoading);
  const adSlotWidthCss = toCssSize(panelWidth) ?? '100%';
  const adSlotHeightCss = toCssSize(panelHeight) ?? '100%';
  const numericAdSlotWidth = toNumericCssSize(panelWidth);
  const numericAdSlotHeight = toNumericCssSize(panelHeight);
  const isUltraCompactAdSlot = isAdSlotSurface && (numericAdSlotHeight ?? Number.POSITIVE_INFINITY) <= 60;
  const isMobileLeaderboardAdSlot =
    isAdSlotSurface &&
    (numericAdSlotWidth ?? Number.POSITIVE_INFINITY) <= 360 &&
    (numericAdSlotHeight ?? Number.POSITIVE_INFINITY) <= 120;

  const setSuggestionButtonRef = useCallback((suggestionId: string) => {
    return (node: HTMLButtonElement | null) => {
      if (node) {
        suggestionButtonRefs.current.set(suggestionId, node);
      } else {
        suggestionButtonRefs.current.delete(suggestionId);
      }
    };
  }, []);

  const trackAssistantEvent = useCallback(
    async (
      event_name: PublisherAssistantAnalyticsEvent['event_name'],
      event: Omit<Partial<PublisherAssistantAnalyticsEvent>, 'event_name' | 'timestamp' | 'session_id'> = {},
    ) => {
      if (!sdk || !sessionId) {
        return;
      }

      try {
        await sdk.trackPublisherAssistantEvent({
          event_name,
          timestamp: new Date().toISOString(),
          session_id: sessionId,
          ...getAssistantPageContext(analyticsPageUrl),
          ...event,
        });
      } catch (analyticsError) {
        console.warn(`[AdMeshIntentAssistant] Failed to track ${event_name}`, analyticsError);
      }
    },
    [analyticsPageUrl, sdk, sessionId],
  );

  const handleRecommendationShown = useCallback(
    (messageId: string, recommendation: PlatformResponse | null, query?: string) => {
      const recommendationId = recommendation?.recommendation_id;
      if (!recommendationId) {
        return;
      }
      const trackKey = `${messageId}:${recommendationId}`;
      if (trackedRecommendationKeysRef.current.has(trackKey)) {
        return;
      }
      trackedRecommendationKeysRef.current.add(trackKey);
      void trackAssistantEvent('recommendation_shown', {
        message_id: messageId,
        query,
        recommendation_id: recommendationId,
        brand_name: recommendation.recommendation?.creative?.brand_name,
        is_sponsored: true,
      });
    },
    [trackAssistantEvent],
  );

  const refreshSuggestionsForResponse = useCallback(
    (
      query: string,
      answer: string,
      sourceLinks?: AssistantSourceLink[],
      relatedItems?: AssistantRelatedItem[],
      recommendation?: PlatformResponse | null,
      followupSuggestions?: Array<{ text: string; type?: Suggestion['type'] }>,
    ) => {
      const generated =
        Array.isArray(followupSuggestions) && followupSuggestions.length
          ? followupSuggestions
              .map((item, index) => {
                const normalizedText = normalizeSuggestionText(item.text || '');
                if (!normalizedText) return null;
                return {
                  id: `followup_llm_${index}_${normalizedText.toLowerCase().replace(/[^a-z0-9]+/g, '_')}`,
                  text: normalizedText,
                  type: item.type || 'information',
                  is_sponsored: false,
                } satisfies Suggestion;
              })
              .filter((item): item is Suggestion => Boolean(item))
              .slice(0, 3)
          : buildFollowupSuggestionsFromAnswer(query, answer, sourceLinks, relatedItems);
      const sponsoredSuggestion = buildSponsoredSuggestionFromRecommendation(recommendation);
      const nextSuggestions = sponsoredSuggestion
        ? [sponsoredSuggestion, ...generated.slice(0, 2)]
        : generated.slice(0, 3);
      if (nextSuggestions.length) {
        setResolvedSuggestions(prioritizeSponsoredSuggestions(nextSuggestions));
      }
    },
    [],
  );

  const upsertSponsoredFollowupSuggestion = useCallback(
    (
      followupSuggestion: {
        query?: string;
        label?: string;
        engagement_url?: string;
        exposure_url?: string;
      },
      recommendationId: string,
    ) => {
      const suggestionText = normalizeSuggestionText(
        followupSuggestion.query || followupSuggestion.label || '',
      );
      if (!suggestionText || !recommendationId) {
        return;
      }

      const sponsoredSuggestion: Suggestion = {
        id: recommendationId,
        text: suggestionText,
        type: 'action',
        is_sponsored: true,
        recommendation: {
          recommendation_id: recommendationId,
          recommendation: {
            creative: {
              follow_up_suggestion: suggestionText,
            },
          },
          tracking: {
            followup_engagement_url: followupSuggestion.engagement_url,
            followup_exposure_url: followupSuggestion.exposure_url,
          },
        } as PlatformResponse,
      };

      setResolvedSuggestions((current) => {
        const organic = current.filter((item) => !item.is_sponsored).slice(0, 3);
        return prioritizeSponsoredSuggestions([sponsoredSuggestion, ...organic].slice(0, 4));
      });
    },
    [],
  );

  const scrollSubmittedMessageToTop = useCallback(() => {
    const scroller = messagesScrollRef.current;
    const activeMessage = activeSubmittedMessageRef.current;
    if (!scroller || !activeMessage) return false;

    const scrollerRect = scroller.getBoundingClientRect();
    const activeMessageRect = activeMessage.getBoundingClientRect();
    const targetTop = Math.max(
      0,
      scroller.scrollTop + activeMessageRect.top - scrollerRect.top,
    );

    scroller.scrollTop = targetTop;
    return true;
  }, []);

  // New submissions pin the submitted query to the top of the active
  // message panel while the answer streams below it.
  useLayoutEffect(() => {
    if (shouldAnchorSubmittedMessageRef.current) {
      if (scrollSubmittedMessageToTop()) {
        shouldAnchorSubmittedMessageRef.current = false;
      }
      return;
    }

    if (suppressAutoScrollToBottomRef.current) return;

    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, scrollSubmittedMessageToTop]);

  useLayoutEffect(() => {
    if (!isAdSlotSurface || isAdSlotOverlayActive) return;
    const root = adSlotRootRef.current;
    if (!root) return;

    const mountNode = root.closest('.admesh-publisher-assistant-reactMount') as HTMLElement | null;
    const nextHeight = adSlotHeightCss;

    [root, mountNode].forEach((node) => {
      if (!node) return;
      node.style.width = adSlotWidthCss;
      node.style.maxWidth = adSlotWidthCss;
      node.style.height = nextHeight;
      node.style.maxHeight = nextHeight;
      node.style.overflow = 'hidden';
      node.style.boxSizing = 'border-box';
      node.dataset.admeshAdSlotExpanded = shouldExpandAdSlot ? 'true' : 'false';
    });

    const host = mountNode?.parentElement;
    if (host && host !== document.body && host !== document.documentElement) {
      host.style.width = adSlotWidthCss;
      host.style.maxWidth = adSlotWidthCss;
      host.style.height = nextHeight;
      host.style.maxHeight = nextHeight;
      host.style.overflow = 'hidden';
      host.style.boxSizing = 'border-box';
    }
  }, [adSlotHeightCss, adSlotWidthCss, isAdSlotOverlayActive, isAdSlotSurface, shouldExpandAdSlot]);

  useEffect(() => {
    if (propSuggestions && propSuggestions.length) {
      setResolvedSuggestions(propSuggestions);
      return;
    }
    if (contextSuggestions && contextSuggestions.length) {
      setResolvedSuggestions((currentSuggestions) =>
        mergeSuggestionsPreservingOrganic(currentSuggestions, contextSuggestions)
      );
    }
  }, [propSuggestions, contextSuggestions]);

  useEffect(() => {
    if (variant !== 'default') return;

    if (!suggestionsLoader) return;
    let cancelled = false;
    suggestionsLoader()
      .then((items) => {
        if (!cancelled && Array.isArray(items) && items.length) {
          setResolvedSuggestions(items);
        }
      })
      .catch((loadError) => {
        console.warn('[AdMeshIntentAssistant] Failed to load suggestions', loadError);
      });
    return () => {
      cancelled = true;
    };
  }, [suggestionsLoader, variant]);

  useEffect(() => {
    if (!isLoading) {
      setGenerationElapsedSec(0);
      generationStartedAtRef.current = null;
      return;
    }

    if (generationStartedAtRef.current == null) {
      generationStartedAtRef.current = typeof performance !== 'undefined' ? performance.now() : Date.now();
    }

    const tick = () => {
      const startedAt = generationStartedAtRef.current;
      if (startedAt == null) {
        return;
      }
      const now = typeof performance !== 'undefined' ? performance.now() : Date.now();
      setGenerationElapsedSec(Math.floor((now - startedAt) / 1000));
    };

    tick();
    const interval = window.setInterval(tick, 1000);
    return () => window.clearInterval(interval);
  }, [isLoading]);

  useEffect(() => {
    if (!isLoading || variant !== 'default') {
      setLoadingLabelIndex(0);
      return;
    }
    const interval = window.setInterval(() => {
      setLoadingLabelIndex((prev) => Math.min(prev + 1, 7));
    }, 3000);
    return () => window.clearInterval(interval);
  }, [isLoading, variant]);

  useEffect(() => {
    if (loadedTrackedRef.current || !sdk || !sessionId) {
      return;
    }
    loadedTrackedRef.current = true;
    void trackAssistantEvent('assistant_loaded', {
      metadata: {
        layout,
        surface,
        variant,
        default_open: defaultOpen || autoOpen,
      },
    });
  }, [autoOpen, defaultOpen, layout, sdk, sessionId, surface, trackAssistantEvent, variant]);

  // Handle open state changes
  const handleOpenChange = useCallback((newOpen: boolean) => {
    if (isControlled) {
      onOpenChange?.(newOpen);
    } else {
      setInternalOpen(newOpen);
    }
    
    if (newOpen) {
      void trackAssistantEvent('assistant_opened', {
        metadata: {
          layout,
          surface,
          ad_slot_behavior: adSlotBehavior,
          variant,
          expanded: isExpanded,
        },
      });
      onOpen?.();
    } else {
      onClose?.();
    }
  }, [isControlled, isExpanded, layout, onOpenChange, onOpen, onClose, surface, trackAssistantEvent, variant]);

  // Handle opening/closing
  const handleOpenClick = useCallback(() => {
    handleOpenChange(true);
  }, [handleOpenChange]);

  const handleCloseClick = useCallback(() => {
    handleOpenChange(false);
  }, [handleOpenChange]);

  const handleToggleExpand = useCallback(() => {
    setIsExpanded(prev => !prev);
  }, []);

  // Call backend API for assistant response with streaming using SDK
  const callAssistantAPI = useCallback(async (
    message: string,
    messageId?: string,
    onContentChunk?: (content: string) => void,
    onStreamStart?: () => void,
    sponsoredRecommendation?: PlatformResponse | null,
    signal?: AbortSignal,
    conversationHistory?: Array<{ role: 'user' | 'assistant'; content: string }>,
  ) => {
    if (!sdk) {
      console.warn('[AdMeshIntentAssistant] SDK not available - using fallback response');
      // Return a fallback response when SDK is not available
      return {
        text: "I'm a demo assistant. The SDK is not properly configured, but I can still help with suggestions! Try clicking on one of the suggested questions above.",
      };
    }
    
    try {
      if (connectedDelegationSession) {
        await refreshDelegationActivity('turn_started', {
          source: 'publisher_assistant',
          recommendation_id: connectedDelegationSession.recommendation_id,
        }).catch((activityError) => {
          console.warn('[AdMeshIntentAssistant] Failed to report delegation turn_started', activityError);
        });
      }

      // Use SDK to make the chat assistant API call
      const response = await fetch(`${sdk.getApiBaseUrl()}/api/chat/assistant`, {
        method: 'POST',
        signal,
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'text/plain',
          'Cache-Control': 'no-cache',
          'Authorization': `Bearer ${apiKey}`,
        },
        body: JSON.stringify({
          message,
          session_id: sessionId || 'default-session',
          message_id: messageId,
          user_id: userId,
          context: {
            timestamp: new Date().toISOString(),
            source: 'admesh-ui-sdk',
            page_context: getAssistantPageContext(analyticsPageUrl),
            conversation_history: conversationHistory,
            sponsored_recommendation: sponsoredRecommendation?.recommendation_id
              ? {
                  recommendation_id: sponsoredRecommendation.recommendation_id,
                  brand_name: sponsoredRecommendation.recommendation?.creative?.brand_name,
                }
              : undefined,
            active_delegation_session: connectedDelegationSession
              ? {
                  delegation_session_id: connectedDelegationSession.delegation_session_id,
                  recommendation_id: connectedDelegationSession.recommendation_id,
                  session_id: connectedDelegationSession.session_id,
                  brand_name: connectedDelegationSession.brand_name,
                  mcp_endpoint: connectedDelegationSession.mcp_endpoint,
                  status: connectedDelegationSession.status,
                }
              : undefined,
          }
        }),
      });

      if (!response.ok) {
        throw new Error(`API request failed: ${response.status} ${response.statusText}`);
      }

      // Handle streaming response
      const reader = response.body?.getReader();
      const decoder = new TextDecoder();
      
      if (!reader) {
        throw new Error('Response body is not readable');
      }

      let fullResponse = '';
      let finalSourceLinks: AssistantSourceLink[] | undefined;
      let finalRelatedItems: AssistantRelatedItem[] | undefined;
      let finalFollowupSuggestions: Array<{ text: string; type?: Suggestion['type'] }> | undefined;
      let finalUsedCapability: string | undefined;
      let finalResponseTimeMs: number | undefined;
      
      const processStream = async () => {
        try {
          while (true) {
            const { done, value } = await reader.read();
            
            if (done) break;
            
            const chunk = decoder.decode(value, { stream: true });
            const lines = chunk.split('\n');
            
            for (const line of lines) {
              if (line.startsWith('data: ')) {
                const data = line.slice(6); // Remove 'data: ' prefix
                
                if (data.trim() === '[DONE]') {
                  if (connectedDelegationSession) {
                    void refreshDelegationActivity('turn_completed', {
                      source: 'publisher_assistant',
                      recommendation_id: connectedDelegationSession.recommendation_id,
                    }).catch((activityError) => {
                      console.warn('[AdMeshIntentAssistant] Failed to report delegation turn_completed', activityError);
                    });
                  }
                  return {
                    text: fullResponse,
                    source_links: finalSourceLinks,
                    related_items: finalRelatedItems,
                    followup_suggestions: finalFollowupSuggestions,
                    used_capability: finalUsedCapability,
                    response_time_ms: finalResponseTimeMs,
                  };
                }
                
                try {
                  const parsedData = JSON.parse(data);
                  
                  switch (parsedData.type) {
                    case 'content': {
                      const content = parsedData.content || '';
                      fullResponse += content;
                      onContentChunk?.(content);
                      break;
                    }
                      
                    case 'final': {
                      fullResponse = parsedData.reply || fullResponse;
                      finalSourceLinks = Array.isArray(parsedData.source_links) ? parsedData.source_links : undefined;
                      finalRelatedItems = Array.isArray(parsedData.related_items) ? parsedData.related_items : undefined;
                      finalFollowupSuggestions = Array.isArray(parsedData.followup_suggestions) ? parsedData.followup_suggestions : undefined;
                      finalUsedCapability = typeof parsedData.used_capability === 'string' ? parsedData.used_capability : finalUsedCapability;
                      finalResponseTimeMs =
                        typeof parsedData.response_time_ms === 'number' ? parsedData.response_time_ms : finalResponseTimeMs;
                      break;
                    }

                    case 'followup_suggestions': {
                      finalFollowupSuggestions = Array.isArray(parsedData.followup_suggestions)
                        ? parsedData.followup_suggestions
                        : finalFollowupSuggestions;
                      break;
                    }
                      
                    case 'error':
                      throw new Error(parsedData.message || 'Unknown streaming error');
                      
                    case 'session_start':
                      onStreamStart?.();
                      console.log('Session started:', parsedData.session_id);
                      break;
                  }
                } catch (parseError) {
                  console.warn('Failed to parse streaming chunk:', data);
                  continue;
                }
              }
            }
          }
        } catch (error) {
          if (signal?.aborted) {
            return {
              text: fullResponse,
              source_links: finalSourceLinks,
              related_items: finalRelatedItems,
              followup_suggestions: finalFollowupSuggestions,
              used_capability: finalUsedCapability,
              response_time_ms: finalResponseTimeMs,
              error: 'aborted',
            };
          }
          console.error('Stream processing error:', error);
          throw error;
        } finally {
          reader.releaseLock();
        }
        
        return {
          text: fullResponse,
          source_links: finalSourceLinks,
          related_items: finalRelatedItems,
          followup_suggestions: finalFollowupSuggestions,
          used_capability: finalUsedCapability,
          response_time_ms: finalResponseTimeMs,
        };
      };
      
      return await processStream();
      
    } catch (error) {
      if (signal?.aborted) {
        return {
          text: '',
          error: 'aborted',
        };
      }
      console.error('Error calling assistant API:', error);
      // Fallback to basic response if API fails
      return {
        text: "I'm having trouble connecting to my services right now. Please try again in a moment.",
        error: error instanceof Error ? error.message : 'request_failed',
      };
    }
  }, [sdk, sessionId, apiKey, userId, connectedDelegationSession, refreshDelegationActivity]);

  const handleStopGeneration = useCallback(() => {
    const controller = generationAbortRef.current;
    if (!controller || controller.signal.aborted) return;
    controller.abort();
    setIsLoading(false);
    currentSponsoredSuggestionRef.current = null;
    currentMessageIsSponsored.current = false;
    const activeAssistantMessageId = activeAssistantMessageIdRef.current;
    if (activeAssistantMessageId) {
      setMessages(prev => prev.map(msg =>
        msg.id === activeAssistantMessageId && !msg.content.trim()
          ? { ...msg, content: 'Generation stopped.' }
          : msg
      ));
    }
  }, []);

  // Handle message sending with streaming
  const handleSendMessage = useCallback(async (message: string) => {
    if (!message.trim() || isLoading) return;
    const startedAt = typeof performance !== 'undefined' ? performance.now() : Date.now();
    const normalizedMessage = message.trim();
    const userMessageId = Date.now().toString();
    const generationController = new AbortController();
    const activeSponsoredSuggestion = currentSponsoredSuggestionRef.current;
    const activeSponsoredRecommendation = activeSponsoredSuggestion?.recommendation || null;
    const conversationHistory = messages
      .filter((msg) => (msg.role === 'user' || msg.role === 'assistant') && msg.content.trim())
      .slice(-6)
      .map((msg) => ({
        role: msg.role as 'user' | 'assistant',
        content: msg.content.trim().slice(0, 1200),
      }));

    generationAbortRef.current = generationController;
    activeAssistantMessageIdRef.current = null;
    activeSubmittedMessageIdRef.current = userMessageId;
    shouldAnchorSubmittedMessageRef.current = true;
    suppressAutoScrollToBottomRef.current = true;
    currentSponsoredSuggestionRef.current = null;
    setResolvedSuggestions((current) => current.some((suggestion) => suggestion.is_sponsored)
      ? current.filter((suggestion) => !suggestion.is_sponsored)
      : current
    );

    // Clear any previous errors
    setError(null);

    const userMessage: ChatMessage = {
      id: userMessageId,
      role: 'user',
      content: normalizedMessage,
      timestamp: new Date(),
    };

    void trackAssistantEvent('query_submitted', {
      message_id: userMessageId,
      query: normalizedMessage,
    });

    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);

    if (variant === 'default' && queryHandler) {
      try {
        const assistantMessageId = (Date.now() + 1).toString();
        const initialAssistantMessage: RenderMessage = {
          id: assistantMessageId,
          role: 'assistant',
          content: '',
          timestamp: new Date(),
          recommendations: [],
          userQuery: message,
          initialRecommendation: null,
        };

        if (currentMessageIsSponsored.current) {
          setSponsoredClickedMessages(prev => new Set([...prev, assistantMessageId]));
          currentMessageIsSponsored.current = false;
        }

        setMessages(prev => [...prev, initialAssistantMessage]);
        activeAssistantMessageIdRef.current = assistantMessageId;

        const response = await queryHandler(normalizedMessage, {
          sponsoredSuggestion: activeSponsoredSuggestion,
        });
        if (generationController.signal.aborted) {
          currentSponsoredSuggestionRef.current = null;
          return;
        }
        const recommendation =
          response.recommendation ||
          activeSponsoredRecommendation ||
          null;

        console.log('[AdMeshIntentAssistant] Query handler completed:', {
          query: message,
          hasResponseRecommendation: !!response.recommendation,
          hasSponsoredSuggestionRecommendation: !!activeSponsoredRecommendation,
          finalRecommendationId: recommendation?.recommendation_id,
        });

        setMessages(prev => prev.map(msg =>
          msg.id === assistantMessageId
            ? {
                ...msg,
                content: response.answer,
                sourceLinks: response.source_links,
                relatedItems: response.related_items,
                initialRecommendation: recommendation,
                responseTimeMs:
                  (typeof performance !== 'undefined' ? performance.now() : Date.now()) - startedAt,
              }
            : msg
        ));
        refreshSuggestionsForResponse(
          normalizedMessage,
          response.answer,
          response.source_links,
          response.related_items,
          response.recommendation || null,
          response.followup_suggestions,
        );
        void trackAssistantEvent('answer_completed', {
          message_id: assistantMessageId,
          query: normalizedMessage,
          recommendation_id: recommendation?.recommendation_id,
          brand_name: recommendation?.recommendation?.creative?.brand_name,
          latency_ms:
            (typeof performance !== 'undefined' ? performance.now() : Date.now()) - startedAt,
          status: 'completed',
          metadata: {
            variant,
            source: 'query_handler',
          },
        });
        currentSponsoredSuggestionRef.current = null;
      } catch (queryError) {
        console.error('Error sending message:', queryError);
        const errorMessage = queryError instanceof Error ? queryError.message : 'Unknown error occurred';
        setError(errorMessage);
        const errorResponse: RenderMessage = {
          id: (Date.now() + 1).toString(),
          role: 'assistant',
          content: 'Sorry, I encountered an error. Please try again.',
          timestamp: new Date(),
        };
        setMessages(prev => [...prev, errorResponse]);
        void trackAssistantEvent('answer_failed', {
          query: normalizedMessage,
          status: errorMessage,
          latency_ms:
            (typeof performance !== 'undefined' ? performance.now() : Date.now()) - startedAt,
          metadata: {
            variant,
            source: 'query_handler',
          },
        });
        currentSponsoredSuggestionRef.current = null;
      } finally {
        if (generationAbortRef.current === generationController) {
          generationAbortRef.current = null;
          activeAssistantMessageIdRef.current = null;
          setIsLoading(false);
        }
      }
      return;
    }

    // If SDK is not available, still show a response for demo purposes
    if (!sdk) {
      // Simulate a brief loading delay for better UX
      setTimeout(() => {
        if (generationController.signal.aborted) {
          if (generationAbortRef.current === generationController) {
            generationAbortRef.current = null;
            activeAssistantMessageIdRef.current = null;
          }
          return;
        }
        const assistantMessage: ChatMessage = {
          id: (Date.now() + 1).toString(),
          role: 'assistant',
          content: "I'm a demo assistant. The SDK is not properly configured, but I can still help! Try clicking on the suggested questions above, or check your AdMesh API key configuration.",
          timestamp: new Date(),
          recommendations: [],
          userQuery: message,
        };

        setMessages(prev => [...prev, assistantMessage]);
        if (generationAbortRef.current === generationController) {
          generationAbortRef.current = null;
          activeAssistantMessageIdRef.current = null;
          setIsLoading(false);
        }
      }, 1000);
      return;
    }

    try {
      // Create an initial assistant message for streaming
      const assistantMessageId = (Date.now() + 1).toString();
      const sponsoredRecommendationForMessage = activeSponsoredRecommendation;
      const initialAssistantMessage: RenderMessage = {
        id: assistantMessageId,
        role: 'assistant',
        content: '',
        timestamp: new Date(),
        recommendations: [],
        userQuery: message,
        initialRecommendation: sponsoredRecommendationForMessage,
      };

      // If this message was triggered by a sponsored suggestion, track it to prevent duplicate recommendations
      if (currentMessageIsSponsored.current) {
        setSponsoredClickedMessages(prev => new Set([...prev, assistantMessageId]));
        currentMessageIsSponsored.current = false; // Reset the flag
      }

      setMessages(prev => [...prev, initialAssistantMessage]);
      activeAssistantMessageIdRef.current = assistantMessageId;

      // Call the streaming backend API
      const response = await callAssistantAPI(
        normalizedMessage,
        userMessageId,
        // Callback for real-time content updates
        (contentChunk: string) => {
          setMessages(prev => prev.map(msg => 
            msg.id === assistantMessageId 
              ? { ...msg, content: msg.content + contentChunk }
              : msg
          ));
        },
        () => {
          setIsLoading(true);
          setLoadingLabelIndex(0);
        },
        sponsoredRecommendationForMessage,
        generationController.signal,
        conversationHistory,
      );
      if (generationController.signal.aborted) {
        if (!response.text.trim()) {
          setMessages(prev => prev.map(msg =>
            msg.id === assistantMessageId && !msg.content.trim()
              ? { ...msg, content: 'Generation stopped.' }
              : msg
          ));
        }
        void trackAssistantEvent('answer_failed', {
          message_id: assistantMessageId,
          query: normalizedMessage,
          recommendation_id: sponsoredRecommendationForMessage?.recommendation_id,
          brand_name: sponsoredRecommendationForMessage?.recommendation?.creative?.brand_name,
          latency_ms:
            (typeof performance !== 'undefined' ? performance.now() : Date.now()) - startedAt,
          status: 'aborted',
          metadata: {
            variant,
            source: 'api_chat_assistant',
          },
        });
        currentSponsoredSuggestionRef.current = null;
        return;
      }
      
      // Final update with complete response
      const resolvedResponseTimeMs =
        typeof response.response_time_ms === 'number'
          ? response.response_time_ms
          : (typeof performance !== 'undefined' ? performance.now() : Date.now()) - startedAt;

      setMessages(prev => prev.map(msg => 
        msg.id === assistantMessageId 
          ? {
              ...msg,
              content: response.text,
              sourceLinks: response.source_links,
              relatedItems: response.related_items,
              initialRecommendation: msg.initialRecommendation || sponsoredRecommendationForMessage,
              responseTimeMs: resolvedResponseTimeMs,
            }
          : msg
      ));
      refreshSuggestionsForResponse(
        normalizedMessage,
        response.text,
        response.source_links,
        response.related_items,
        null,
        response.followup_suggestions,
      );
      if (response.error) {
        void trackAssistantEvent('answer_failed', {
          message_id: assistantMessageId,
          query: normalizedMessage,
          recommendation_id: sponsoredRecommendationForMessage?.recommendation_id,
          brand_name: sponsoredRecommendationForMessage?.recommendation?.creative?.brand_name,
          latency_ms:
            (typeof performance !== 'undefined' ? performance.now() : Date.now()) - startedAt,
          status: response.error,
          metadata: {
            variant,
            source: 'api_chat_assistant',
          },
        });
      } else {
        void trackAssistantEvent('answer_completed', {
          message_id: assistantMessageId,
          query: normalizedMessage,
          recommendation_id: sponsoredRecommendationForMessage?.recommendation_id,
          brand_name: sponsoredRecommendationForMessage?.recommendation?.creative?.brand_name,
          used_capability: response.used_capability,
          latency_ms: resolvedResponseTimeMs,
          status: 'completed',
          metadata: {
            variant,
            source: 'api_chat_assistant',
            response_time_ms: resolvedResponseTimeMs,
          },
        });
      }
      currentSponsoredSuggestionRef.current = null;
      
    } catch (error) {
      console.error('Error sending message:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      setError(errorMessage);
      
      const errorResponse: ChatMessage = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: 'Sorry, I encountered an error. Please try again.',
        timestamp: new Date(),
      };
      setMessages(prev => [...prev, errorResponse]);
      void trackAssistantEvent('answer_failed', {
        query: normalizedMessage,
        latency_ms:
          (typeof performance !== 'undefined' ? performance.now() : Date.now()) - startedAt,
        status: errorMessage,
        metadata: {
          variant,
          source: 'api_chat_assistant',
        },
      });
      currentSponsoredSuggestionRef.current = null;
    } finally {
      if (generationAbortRef.current === generationController) {
        generationAbortRef.current = null;
        activeAssistantMessageIdRef.current = null;
        setIsLoading(false);
      }
    }
  }, [isLoading, callAssistantAPI, queryHandler, refreshSuggestionsForResponse, sdk, trackAssistantEvent, variant]);

  // Use prop suggestions or fallback to context suggestions
  const suggestions = resolvedSuggestions;

  // Check if we have any sponsored suggestions (for onlyShowWhenSponsored logic)
  const hasSponsoredSuggestions = useMemo(
    () => suggestions.some(s => s?.is_sponsored),
    [suggestions]
  );

  // Filter suggestions based on the onlyShowWhenSponsored option
  const safeSuggestions = useMemo(
    () => suggestions.filter(
      (suggestion): suggestion is Suggestion => {
        const isValid = Boolean(suggestion && typeof suggestion.id === 'string');
        if (!isValid) return false;

        // If onlyShowWhenSponsored is true, only show suggestions if we have at least one sponsored
        if (onlyShowWhenSponsored) {
          // Return all valid suggestions, but only if we have at least one sponsored
          return hasSponsoredSuggestions;
        }

        // Otherwise, include all valid suggestions
        return true;
      }
    ),
    [hasSponsoredSuggestions, onlyShowWhenSponsored, suggestions]
  );
  const orderedSuggestions = useMemo(
    () => prioritizeSponsoredSuggestions(safeSuggestions),
    [safeSuggestions]
  );

  // Handle suggestion click
  const handleSuggestionClick = useCallback(async (suggestion: Suggestion) => {
    console.log('[AdMeshIntentAssistant] 🖱️ Suggestion clicked:', {
      text: suggestion.text,
      is_sponsored: suggestion.is_sponsored,
      hasRecommendation: !!suggestion.recommendation,
      recommendationId: suggestion.recommendation?.recommendation_id || undefined,
      hasFollowupEngagementUrl: !!suggestion.recommendation?.tracking?.followup_engagement_url,
      sessionId: sessionId
    });

    // Set sponsored flag for the next message immediately
    currentMessageIsSponsored.current = suggestion.is_sponsored || false;
    currentSponsoredSuggestionRef.current = suggestion.is_sponsored ? suggestion : null;
    const suggestionPosition = orderedSuggestions.findIndex((item) => item.id === suggestion.id);
    void trackAssistantEvent('suggestion_clicked', {
      suggestion_id: suggestion.id,
      suggestion_text: suggestion.text,
      suggestion_type: suggestion.type,
      suggestion_position: suggestionPosition >= 0 ? suggestionPosition : undefined,
      is_sponsored: suggestion.is_sponsored,
      recommendation_id: suggestion.recommendation?.recommendation_id,
      brand_name: suggestion.recommendation?.recommendation?.creative?.brand_name,
      query: suggestion.text,
    });
    
    // Send the message immediately for instant response
    handleSendMessage(suggestion.text);
    
    // Fire engagement URL for sponsored suggestions asynchronously (non-blocking)
    if (suggestion.is_sponsored && suggestion.recommendation && sdk) {
      // Use setTimeout to make this non-blocking
      setTimeout(async () => {
        try {
          const followupEngagementUrl = suggestion.recommendation!.tracking?.followup_engagement_url;
          const recommendationId = suggestion.recommendation!.recommendation_id;
          
          console.log('[AdMeshIntentAssistant] 🎯 Preparing to fire sponsored suggestion engagement:', {
            followupEngagementUrl,
            recommendationId,
            sessionId
          });
          
          if (followupEngagementUrl && recommendationId && sessionId) {
            console.log('[AdMeshIntentAssistant] 🎯 Firing sponsored suggestion engagement tracking');
            await sdk.fireFollowupEngagement(followupEngagementUrl, recommendationId, sessionId);
            console.log('[AdMeshIntentAssistant] ✅ Engagement tracking fired successfully');
            
          } else {
            console.warn('[AdMeshIntentAssistant] ⚠️ Missing required data for engagement tracking:', {
              hasFollowupEngagementUrl: !!followupEngagementUrl,
              hasRecommendationId: !!recommendationId,
              hasSessionId: !!sessionId
            });
          }
        } catch (error) {
          console.error('[AdMeshIntentAssistant] ❌ Error firing sponsored suggestion engagement:', error);
          // Don't block the user action if engagement tracking fails
        }
      }, 0); // Use setTimeout(0) to make it async but execute immediately
    } else {
      console.log('[AdMeshIntentAssistant] ℹ️ Not a sponsored suggestion or missing data, skipping engagement tracking');
    }
  }, [handleSendMessage, orderedSuggestions, sdk, sessionId, trackAssistantEvent]);

  // Position and size styles
  const positionStyle = positionConfig[position];
  const sizeStyle = sizeConfig[size];

  // Track suggestion exposure only after the suggestion is actually viewable.
  useEffect(() => {
    suggestionExposureCleanupRef.current.forEach((cleanup) => cleanup());
    suggestionExposureCleanupRef.current = [];

    if (typeof window === 'undefined' || typeof IntersectionObserver === 'undefined') {
      return;
    }

    orderedSuggestions.forEach((suggestion, index) => {
      const element = suggestionButtonRefs.current.get(suggestion.id);
      if (!element) {
        return;
      }

      const exposureKey = `${sessionId || 'anonymous'}:${suggestion.id}:${suggestion.is_sponsored ? 's' : 'o'}`;
      if (trackedSuggestionExposureKeysRef.current.has(exposureKey)) {
        return;
      }

      let viewableTimer: ReturnType<typeof setTimeout> | null = null;
      let observer: IntersectionObserver | null = null;

      const cleanup = () => {
        if (viewableTimer) {
          clearTimeout(viewableTimer);
          viewableTimer = null;
        }
        observer?.disconnect();
        observer = null;
      };

      const fireSuggestionExposure = () => {
        if (trackedSuggestionExposureKeysRef.current.has(exposureKey)) {
          cleanup();
          return;
        }

        trackedSuggestionExposureKeysRef.current.add(exposureKey);
        cleanup();

        void trackAssistantEvent('suggestion_exposure', {
          suggestion_id: suggestion.id,
          suggestion_text: suggestion.text,
          suggestion_type: suggestion.type,
          suggestion_position: index,
          is_sponsored: suggestion.is_sponsored,
          recommendation_id: suggestion.recommendation?.recommendation_id,
          brand_name: suggestion.recommendation?.recommendation?.creative?.brand_name,
        });

        if (sdk && sessionId && suggestion.is_sponsored && suggestion.recommendation) {
          const exposureUrl = suggestion.recommendation.tracking?.followup_exposure_url;
          const recommendationId = suggestion.recommendation.recommendation_id;

          if (exposureUrl && recommendationId) {
            console.log('[AdMeshIntentAssistant] 🎯 Firing viewable sponsored suggestion exposure:', {
              exposureUrl,
              recommendationId,
              suggestionText: suggestion.text,
              sessionId
            });

            sdk.fireFollowupExposure(exposureUrl, recommendationId, sessionId);
          } else {
            console.warn('[AdMeshIntentAssistant] ⚠️ Missing exposure URL or recommendation ID for sponsored suggestion:', {
              hasExposureUrl: !!exposureUrl,
              hasRecommendationId: !!recommendationId,
              suggestionText: suggestion.text
            });
          }
        }
      };

      observer = new IntersectionObserver(
        (entries) => {
          const entry = entries[0];
          if (!entry) {
            return;
          }

          if (entry.intersectionRatio >= 0.5) {
            if (!viewableTimer) {
              viewableTimer = setTimeout(fireSuggestionExposure, 1000);
            }
            return;
          }

          if (viewableTimer) {
            clearTimeout(viewableTimer);
            viewableTimer = null;
          }
        },
        { threshold: [0, 0.25, 0.5, 0.75, 1], rootMargin: '0px' }
      );

      observer.observe(element);
      suggestionExposureCleanupRef.current.push(cleanup);
    });

    return () => {
      suggestionExposureCleanupRef.current.forEach((cleanup) => cleanup());
      suggestionExposureCleanupRef.current = [];
    };
  }, [orderedSuggestions, sdk, sessionId, trackAssistantEvent]);

  // Debug log received suggestions and analysis state
  console.log('[AdMeshIntentAssistant] 📥 Received suggestions:', {
    totalSuggestions: suggestions.length,
    safeSuggestions: safeSuggestions.length,
    isAnalyzingIntent,
    hasIntentError: !!intentError,
    onlyShowWhenSponsored,
    hasSponsoredSuggestions,
    sponsoredCount: suggestions.filter(s => s?.is_sponsored).length,
    nonSponsoredCount: suggestions.filter(s => s && !s?.is_sponsored).length,
    modalShouldAppear: onlyShowWhenSponsored ? hasSponsoredSuggestions : true,
    suggestions: suggestions.map(s => ({
      id: s?.id,
      text: s?.text,
      type: s?.type,
      is_sponsored: s?.is_sponsored,
      hasRecommendation: !!s?.recommendation,
      recommendationId: s?.recommendation?.recommendation_id || undefined,
      hasFollowupEngagementUrl: !!s?.recommendation?.tracking?.followup_engagement_url
    })),
    renderedSuggestions: orderedSuggestions.map(s => ({
      id: s?.id,
      text: s?.text,
      type: s?.type,
      is_sponsored: s?.is_sponsored,
      hasRecommendation: !!s?.recommendation,
      recommendationId: s?.recommendation?.recommendation_id || undefined,
      hasFollowupEngagementUrl: !!s?.recommendation?.tracking?.followup_engagement_url
    }))
  });
  const fontSizes = {
    base: `${16 * fontScale}px`,
    sm: `${14 * fontScale}px`,
    xs: `${12 * fontScale}px`,
  };
  const genericDisclaimer = disclaimerText || 'AI-generated responses may contain errors. Please verify important information independently.';
  const loadingSteps = [
    'Searching trusted sources...',
    'Reviewing publisher coverage...',
    'Checking relevant context...',
    'Comparing supporting signals...',
    'Identifying the strongest examples...',
    'Organizing the most useful details...',
    'Shaping the response for clarity...',
    'Preparing the clearest answer...',
    'Finishing the response...',
  ];

  const renderGeneratingIndicator = (options?: {
    compact?: boolean;
    accentColor?: string;
    metaColor?: string;
    includeSteps?: boolean;
  }) => {
    const accent = options?.accentColor || publisherAccent;
    const meta = options?.metaColor || publisherMeta;
    const includeSteps = options?.includeSteps ?? variant === 'default';
    const timerLabel = `${generationElapsedSec}s`;
    const statusLabel = includeSteps
      ? `${loadingSteps[loadingLabelIndex]} (${timerLabel})`
      : `Generating answer... ${timerLabel}`;

    return (
      <div
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: options?.compact ? '8px' : '10px',
          color: meta,
          fontSize: options?.compact ? fontSizes.xs : fontSizes.sm,
        }}
      >
        <span
          style={{
            width: options?.compact ? 8 : 9,
            height: options?.compact ? 8 : 9,
            borderRadius: 999,
            background: accent,
            animation: 'admeshPulse 1.4s ease-in-out infinite',
            boxShadow: `0 0 0 0 ${withAlpha(brandPrimary, 0.28)}`,
            flexShrink: 0,
          }}
        />
        <span>{statusLabel}</span>
      </div>
    );
  };

  const handleAnswerFeedback = useCallback(
    (message: RenderMessage, helpful: boolean) => {
      if (messageFeedback[message.id]) {
        return;
      }

      const feedbackValue: AnswerFeedbackValue = helpful ? 'helpful' : 'not_helpful';
      setMessageFeedback((prev) => ({ ...prev, [message.id]: feedbackValue }));
      void trackAssistantEvent('answer_feedback', {
        message_id: message.id,
        query: message.userQuery,
        status: feedbackValue,
        latency_ms: message.responseTimeMs,
        metadata: {
          helpful,
        },
      });
    },
    [messageFeedback, trackAssistantEvent],
  );

  function renderAnswerFooter(message: RenderMessage, options?: { accentColor?: string; metaColor?: string }) {
    if (message.role !== 'assistant' || !message.content.trim()) {
      return null;
    }

    if (isLoading && message.id === activeAssistantMessageIdRef.current) {
      return null;
    }

    const accent = options?.accentColor || publisherAccent;
    const meta = options?.metaColor || publisherMeta;
    const selectedFeedback = messageFeedback[message.id];
    const hasTiming = typeof message.responseTimeMs === 'number';

    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '12px',
          marginTop: '10px',
          paddingTop: '10px',
          borderTop: `1px solid ${withAlpha(brandPrimary, 0.08)}`,
          flexWrap: 'wrap',
        }}
      >
        <div
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            minWidth: 0,
            flex: '1 1 auto',
            flexWrap: 'wrap',
          }}
        >
          <span style={{ fontSize: fontSizes.xs, color: meta, whiteSpace: 'nowrap' }}>
            {selectedFeedback ? 'Thanks for your feedback' : 'Is this helpful?'}
          </span>
          {!selectedFeedback ? (
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
              <button
                type="button"
                aria-label="Mark answer as helpful"
                onClick={() => handleAnswerFeedback(message, true)}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 28,
                  height: 28,
                  borderRadius: 999,
                  border: `1px solid ${withAlpha(brandPrimary, 0.12)}`,
                  background: '#ffffff',
                  color: meta,
                  cursor: 'pointer',
                }}
              >
                <ThumbsUp size={14} />
              </button>
              <button
                type="button"
                aria-label="Mark answer as not helpful"
                onClick={() => handleAnswerFeedback(message, false)}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 28,
                  height: 28,
                  borderRadius: 999,
                  border: `1px solid ${withAlpha(brandPrimary, 0.12)}`,
                  background: '#ffffff',
                  color: meta,
                  cursor: 'pointer',
                }}
              >
                <ThumbsDown size={14} />
              </button>
            </div>
          ) : (
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 28,
                height: 28,
                borderRadius: 999,
                background: withAlpha(accent, 0.12),
                color: accent,
                flexShrink: 0,
              }}
            >
              {selectedFeedback === 'helpful' ? <ThumbsUp size={14} /> : <ThumbsDown size={14} />}
            </span>
          )}
        </div>
        {hasTiming ? (
          <span
            style={{
              fontSize: fontSizes.xs,
              color: meta,
              whiteSpace: 'nowrap',
              flex: '0 1 auto',
              marginLeft: 'auto',
            }}
          >
            Answer Completed in {formatGenerationDurationFromMs(message.responseTimeMs!)}
          </span>
        ) : null}
      </div>
    );
  }

  const updateSuggestionScrollState = useCallback(() => {
    const node = suggestionsScrollRef.current;
    if (!node) return;
    const maxScrollLeft = Math.max(0, node.scrollWidth - node.clientWidth);
    setCanScrollSuggestionsLeft(node.scrollLeft > 4);
    setCanScrollSuggestionsRight(node.scrollLeft < maxScrollLeft - 4);
  }, []);

  useEffect(() => {
    updateSuggestionScrollState();
    window.addEventListener('resize', updateSuggestionScrollState);
    return () => window.removeEventListener('resize', updateSuggestionScrollState);
  }, [updateSuggestionScrollState, orderedSuggestions.length]);

  const scrollSuggestions = useCallback((direction: 'left' | 'right') => {
    const node = suggestionsScrollRef.current;
    if (!node) return;
    node.scrollBy({
      left: direction === 'left' ? -Math.max(160, Math.floor(node.clientWidth * 0.75)) : Math.max(160, Math.floor(node.clientWidth * 0.75)),
      behavior: 'smooth',
    });
  }, []);

  const withAlpha = (hex: string | undefined, alpha: number) => {
    if (!hex || !hex.startsWith('#')) return `rgba(15, 23, 42, ${alpha})`;
    const raw = hex.slice(1);
    const full = raw.length === 3 ? raw.split('').map((char) => `${char}${char}`).join('') : raw;
    if (full.length !== 6) return hex;
    const red = parseInt(full.slice(0, 2), 16);
    const green = parseInt(full.slice(2, 4), 16);
    const blue = parseInt(full.slice(4, 6), 16);
    return `rgba(${red}, ${green}, ${blue}, ${alpha})`;
  };

  const brandPrimary = theme.primaryColor || '#3F037F';
  const brandSurface = theme.surfaceColor || '#ffffff';
  const brandBackground = theme.backgroundColor || '#eef2ff';
  const brandBorder = theme.borderColor || '#e5e7eb';
  const brandText = theme.textColor || '#111827';
  const brandMuted = theme.textSecondaryColor || '#6b7280';
  const brandAccentSoft = withAlpha(brandPrimary, 0.12);
  const purpleBorderSoft = withAlpha(brandPrimary, 0.24);
  const purpleBorderStrong = withAlpha(brandPrimary, 0.38);
  const purpleShadowSoft = withAlpha(brandPrimary, 0.12);
  const publisherSurfaceBg = `linear-gradient(180deg, ${withAlpha(brandPrimary, 0.04)} 0%, ${withAlpha(brandPrimary, 0.08)} 100%)`;
  const publisherAccent = brandPrimary;
  const sendButtonTextColor = theme.primaryTextColor || '#ffffff';
  const publisherMuted = brandMuted;
  const publisherMeta = brandMuted;
  const assistantSubtitle = subtitle || 'Publisher Assistant';
  const shouldShowSuggestionSkeletons = isAnalyzingIntent && orderedSuggestions.length === 0 && messages.length === 0;
  const expandedShellBackground = '#f7f8fc';
  const expandedPanelBackground = '#ffffff';
  const expandedHeaderBackground = 'linear-gradient(180deg, rgba(255,255,255,0.96) 0%, rgba(248,250,252,0.98) 100%)';
  const expandedBorder = 'rgba(15, 23, 42, 0.08)';
  const expandedBorderStrong = 'rgba(15, 23, 42, 0.12)';
  const expandedShadow = '0 24px 60px rgba(15, 23, 42, 0.16)';
  const expandedMutedText = '#667085';
  const expandedSubtleText = '#98a2b3';
  const expandedUserBubble = publisherAccent;
  const expandedAssistantBubble = '#f8fafc';
  const expandedChipBackground = '#f8fafc';
  const expandedChipBorder = 'rgba(15, 23, 42, 0.08)';
  const expandedComposerBackground = '#ffffff';
  const expandedComposerShadow = '0 8px 24px rgba(15, 23, 42, 0.08)';
  const expandedOverlay = 'rgba(15, 23, 42, 0.18)';

  function renderDelegationBanner(mode: 'default' | 'legacy') {
    if (!connectedDelegationSession) {
      return null;
    }

    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '12px',
          padding: mode === 'default' ? '10px 12px' : '8px 10px',
          borderRadius: mode === 'default' ? '14px' : '12px',
          border: `1px solid ${purpleBorderSoft}`,
          background: withAlpha('#ffffff', 0.9),
          marginBottom: '4px',
        }}
      >
        <div style={{ minWidth: 0 }}>
          <div
            style={{
              color: publisherAccent,
              fontSize: fontSizes.xs,
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '0.06em',
            }}
          >
            Agent Connected
          </div>
          <div style={{ color: brandText, fontSize: fontSizes.sm, fontWeight: 600, lineHeight: 1.3 }}>
            {connectedDelegationSession.brand_name} agent is active
          </div>
        </div>
        <button
          type="button"
          onClick={() => {
            void stopDelegationSession('user_stopped', { source: 'publisher_assistant' });
          }}
          style={{
            border: `1px solid ${purpleBorderSoft}`,
            borderRadius: 999,
            background: withAlpha('#ffffff', 0.92),
            color: publisherAccent,
            padding: '7px 12px',
            fontSize: fontSizes.xs,
            fontWeight: 700,
            cursor: 'pointer',
            whiteSpace: 'nowrap',
          }}
        >
          Stop
        </button>
      </div>
    );
  }

  function renderExpandedBranding(mode: 'default' | 'legacy', compact = isAdSlotSurface) {
    const mobileExpanded = isMobileViewport && !compact;
    const centeredBranding = compact || mobileExpanded;
    const poweredFontSize = compact ? '9.5px' : mobileExpanded ? '10px' : '11px';
    const disclaimerFontSize = compact ? '9px' : mobileExpanded ? '9px' : '11px';

    return (
      <div
        style={{
          display: 'flex',
          flexDirection: centeredBranding ? 'column' : 'row',
          alignItems: centeredBranding ? 'center' : mode === 'default' ? 'center' : 'flex-start',
          justifyContent: centeredBranding ? 'center' : 'space-between',
          gap: centeredBranding ? (mobileExpanded ? '4px' : '2px') : '12px',
          flexWrap: 'wrap',
          paddingTop: compact ? '2px' : mode === 'default' ? '2px' : '8px',
          borderTop: mode === 'default' ? `1px solid ${expandedBorder}` : 'none',
          marginTop: compact ? '0' : mode === 'default' ? '4px' : 0,
          flexShrink: 0,
          textAlign: centeredBranding ? 'center' : undefined,
          width: centeredBranding ? '100%' : undefined,
        }}
      >
        <div
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: centeredBranding ? 'center' : undefined,
            gap: compact ? '4px' : '8px',
            color: expandedSubtleText,
            fontSize: poweredFontSize,
            fontWeight: 700,
            letterSpacing: '0.01em',
          }}
        >
          <span>
            Powered by{' '}
            <a
              href="https://useadmesh.com"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: 'inherit', textDecoration: 'none', fontWeight: 700 }}
            >
              AdMesh
            </a>
          </span>
        </div>
        <div
          style={{
            color: expandedSubtleText,
            fontSize: disclaimerFontSize,
            lineHeight: centeredBranding ? 1.3 : 1.45,
            maxWidth: centeredBranding ? '100%' : mode === 'default' ? '68%' : '100%',
            textAlign: centeredBranding ? 'center' : mode === 'default' ? 'right' : 'left',
          }}
        >
          {genericDisclaimer}
        </div>
      </div>
    );
  }

  function renderSuggestionSkeletons(mode: 'publisher' | 'legacy') {
    const skeletonWidths = mode === 'publisher' ? [180, 220, 196] : ['100%', '82%', '68%'];
    if (mode === 'publisher') {
      return (
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <div
            style={{
              width: 28,
              height: 28,
              borderRadius: 999,
              background: withAlpha(brandPrimary, 0.10),
              flexShrink: 0,
            }}
          />
          <div
            style={{
              display: 'flex',
              gap: '10px',
              overflow: 'hidden',
              flex: 1,
            }}
          >
            {skeletonWidths.map((width, index) => (
              <div
                key={`suggestion-skeleton-${index}`}
                style={{
                  width,
                  maxWidth: '100%',
                  height: 40,
                  borderRadius: 999,
                  background: `linear-gradient(90deg, ${withAlpha(brandPrimary, 0.08)} 0%, ${withAlpha(brandPrimary, 0.18)} 50%, ${withAlpha(brandPrimary, 0.08)} 100%)`,
                  backgroundSize: '200% 100%',
                  animation: 'admeshShimmer 1.4s ease-in-out infinite',
                  boxShadow: `0 8px 22px ${withAlpha(brandPrimary, 0.08)}`,
                  flexShrink: 0,
                }}
              />
            ))}
          </div>
          <div
            style={{
              width: 28,
              height: 28,
              borderRadius: 999,
              background: withAlpha(brandPrimary, 0.10),
              flexShrink: 0,
            }}
          />
        </div>
      );
    }

    return (
      <div
        aria-hidden="true"
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: mode === 'publisher' ? '10px' : '12px',
        }}
      >
        {skeletonWidths.map((width, index) => (
          <div
            key={`suggestion-skeleton-${index}`}
            style={{
              width,
              maxWidth: '100%',
              height: mode === 'publisher' ? 40 : 52,
              borderRadius: 999,
              background: `linear-gradient(90deg, ${withAlpha(brandPrimary, 0.08)} 0%, ${withAlpha(brandPrimary, 0.18)} 50%, ${withAlpha(brandPrimary, 0.08)} 100%)`,
              backgroundSize: '200% 100%',
              animation: 'admeshShimmer 1.4s ease-in-out infinite',
              boxShadow: mode === 'publisher' ? `0 8px 22px ${withAlpha(brandPrimary, 0.08)}` : 'none',
            }}
          />
        ))}
      </div>
    );
  }

  function renderPublisherSuggestions(compact = isAdSlotSurface) {
    if (isLoading) return null;
    if (shouldShowSuggestionSkeletons) {
      return renderSuggestionSkeletons('publisher');
    }
    if (!orderedSuggestions.length) return null;
    const ultraCompact = compact && isUltraCompactAdSlot;
    const visibleSuggestions = orderedSuggestions.slice(0, maxSuggestions);
    if (suggestionStyle === 'stacked') {
      return (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: compact ? '6px' : '8px',
            flexShrink: 0,
            overflowY: 'auto',
            maxHeight: compact ? '240px' : undefined,
            paddingRight: compact ? '2px' : 0,
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
          }}
        >
          {visibleSuggestions.map((suggestion) => (
            <button
              key={suggestion.id}
              ref={setSuggestionButtonRef(suggestion.id)}
              onClick={() => {
                if (variant === 'default') {
                  setIsExpanded(true);
                  if (layout === 'floating') {
                    handleOpenChange(true);
                  }
                }
                handleSuggestionClick(suggestion);
              }}
              type="button"
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '8px',
                width: '100%',
                minWidth: 0,
                borderRadius: compact ? '12px' : '14px',
                border: suggestion.is_sponsored
                  ? `1px solid ${withAlpha(brandPrimary, 0.32)}`
                  : `1px solid ${withAlpha(brandPrimary, 0.42)}`,
                background: suggestion.is_sponsored ? brandPrimary : withAlpha('#ffffff', 0.94),
                color: suggestion.is_sponsored ? '#ffffff' : brandText,
                padding: compact ? '8px 10px' : '10px 14px',
                fontSize: compact ? '12px' : '13px',
                fontWeight: 500,
                lineHeight: 1.25,
                textAlign: 'left',
                boxShadow: suggestion.is_sponsored
                  ? `0 8px 18px ${withAlpha(brandPrimary, 0.14)}`
                  : '0 4px 12px rgba(15, 23, 42, 0.05)',
                cursor: 'pointer',
              }}
            >
              <span style={{ minWidth: 0 }}>{suggestion.text}</span>
              {adsEnabled && suggestion.is_sponsored && (
                <span
                  style={{
                    flexShrink: 0,
                    borderRadius: 999,
                    background: withAlpha('#ffffff', 0.18),
                    padding: '2px 6px',
                    fontSize: '10px',
                    fontWeight: 700,
                  }}
                >
                  Ad
                </span>
              )}
            </button>
          ))}
        </div>
      );
    }
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: ultraCompact ? '4px' : isMobileLeaderboardAdSlot ? '5px' : compact ? '6px' : '10px', flexShrink: 0 }}>
        {!ultraCompact && (
          <button
            type="button"
            onClick={() => scrollSuggestions('left')}
            disabled={!canScrollSuggestionsLeft}
            aria-label="Scroll suggestions left"
            style={{
              width: isMobileLeaderboardAdSlot ? 20 : compact ? 24 : 28,
              height: isMobileLeaderboardAdSlot ? 20 : compact ? 24 : 28,
              borderRadius: 999,
              border: `1px solid ${withAlpha(brandBorder, 0.8)}`,
              background: withAlpha('#ffffff', 0.9),
              color: '#6b7280',
              fontSize: isMobileLeaderboardAdSlot ? '13px' : undefined,
              lineHeight: 1,
              cursor: canScrollSuggestionsLeft ? 'pointer' : 'default',
              opacity: canScrollSuggestionsLeft ? 1 : 0.55,
            }}
          >
            ‹
          </button>
        )}
        <div
          ref={suggestionsScrollRef}
          onScroll={updateSuggestionScrollState}
          style={{
            display: 'flex',
            gap: ultraCompact ? '4px' : isMobileLeaderboardAdSlot ? '5px' : compact ? '6px' : '10px',
            overflowX: 'auto',
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
            flex: 1,
          }}
        >
          {visibleSuggestions.map((suggestion) => (
            <button
              key={suggestion.id}
              ref={setSuggestionButtonRef(suggestion.id)}
              onClick={() => {
                if (variant === 'default') {
                  setIsExpanded(true);
                  if (layout === 'floating') {
                    handleOpenChange(true);
                  }
                }
                handleSuggestionClick(suggestion);
              }}
              type="button"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: ultraCompact ? '4px' : isMobileLeaderboardAdSlot ? '5px' : '8px',
                whiteSpace: 'nowrap',
                borderRadius: 999,
                border: suggestion.is_sponsored
                  ? `1px solid ${withAlpha(brandPrimary, 0.32)}`
                  : `1px solid ${withAlpha(brandPrimary, 0.42)}`,
                background: suggestion.is_sponsored ? brandPrimary : withAlpha('#ffffff', 0.94),
                color: suggestion.is_sponsored ? '#ffffff' : brandText,
                padding: ultraCompact ? '4px 8px' : isMobileLeaderboardAdSlot ? '5px 8px' : compact ? '7px 10px' : '8px 14px',
                fontSize: ultraCompact ? '10px' : isMobileLeaderboardAdSlot ? '11px' : compact ? '12px' : '13px',
                fontWeight: 500,
                lineHeight: ultraCompact ? 1 : isMobileLeaderboardAdSlot ? 1.1 : undefined,
                boxShadow: suggestion.is_sponsored
                  ? `0 8px 18px ${withAlpha(brandPrimary, 0.14)}`
                  : '0 4px 12px rgba(15, 23, 42, 0.05)',
                cursor: 'pointer',
              }}
            >
              <span>{suggestion.text}</span>
              {adsEnabled && suggestion.is_sponsored && (
                <sup style={{ fontSize: '10px', opacity: 0.9 }}>Ad</sup>
              )}
            </button>
          ))}
        </div>
        {!ultraCompact && (
          <button
            type="button"
            onClick={() => scrollSuggestions('right')}
            disabled={!canScrollSuggestionsRight}
            aria-label="Scroll suggestions right"
            style={{
              width: isMobileLeaderboardAdSlot ? 20 : compact ? 24 : 28,
              height: isMobileLeaderboardAdSlot ? 20 : compact ? 24 : 28,
              borderRadius: 999,
              border: `1px solid ${withAlpha(brandBorder, 0.8)}`,
              background: withAlpha('#ffffff', 0.9),
              color: '#6b7280',
              fontSize: isMobileLeaderboardAdSlot ? '13px' : undefined,
              lineHeight: 1,
              cursor: canScrollSuggestionsRight ? 'pointer' : 'default',
              opacity: canScrollSuggestionsRight ? 1 : 0.55,
            }}
          >
            ›
          </button>
        )}
      </div>
    );
  }

  function renderSourceLinks(message: RenderMessage) {
    if (message.relatedItems?.length) return null;
    if (!message.sourceLinks?.length) return null;
    return (
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '12px' }}>
        {message.sourceLinks.map((link) => (
          <a
            key={`${message.id}-${link.url}`}
            href={link.url}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => {
              void trackAssistantEvent('source_link_clicked', {
                message_id: message.id,
                query: message.userQuery,
                metadata: {
                  source_title: link.title,
                  source_url: link.url,
                },
              });
            }}
            style={{
              color: publisherAccent,
              fontSize: fontSizes.xs,
              textDecoration: 'none',
              borderBottom: `1px solid ${withAlpha(brandPrimary, 0.18)}`,
            }}
          >
            {link.title || link.url}
          </a>
        ))}
      </div>
    );
  }

  function renderRelatedItems(message: RenderMessage, compact = isAdSlotSurface) {
    if (!message.relatedItems?.length) return null;
    return (
      <div style={{ marginTop: '14px' }}>
        <div style={{ fontSize: fontSizes.xs, fontWeight: 700, color: '#4b5563', marginBottom: '8px' }}>Related</div>
        <div
          style={{
            display: 'flex',
            gap: compact ? '8px' : '10px',
            overflowX: 'auto',
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
            padding: '2px 2px 6px',
            maxWidth: '100%',
            scrollSnapType: 'x proximity',
          }}
        >
          {message.relatedItems.slice(0, 4).map((item) => (
            <a
              key={`${message.id}-${item.url}`}
              href={item.url}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => {
                void trackAssistantEvent('source_link_clicked', {
                  message_id: message.id,
                  query: message.userQuery,
                  metadata: {
                    source_title: item.title,
                    source_url: item.url,
                    source_publisher: item.publisher,
                    source_type: 'related_item',
                  },
                });
              }}
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: compact ? '6px' : '10px',
                padding: compact ? '10px' : '14px',
                borderRadius: compact ? '12px' : '14px',
                textDecoration: 'none',
                color: brandText,
                border: `1px solid ${withAlpha(brandPrimary, 0.18)}`,
                background: '#ffffff',
                boxShadow: '0 10px 24px rgba(15, 23, 42, 0.08)',
                flex: compact
                  ? '0 0 min(220px, calc(100% - 8px))'
                  : '0 0 min(250px, calc(100% - 16px))',
                minWidth: 0,
                maxWidth: compact ? '220px' : '250px',
                flexShrink: 0,
                scrollSnapAlign: 'start',
                boxSizing: 'border-box',
              }}
            >
              <div
                style={{
                  fontSize: compact ? '13px' : '18px',
                  fontWeight: 650,
                  lineHeight: 1.35,
                  color: brandPrimary,
                  overflowWrap: 'anywhere',
                }}
              >
                {item.title || item.url}
              </div>
              {item.publisher && (
                <div
                  style={{
                    fontSize: compact ? '11px' : '15px',
                    color: '#6b7280',
                    lineHeight: 1.4,
                    overflowWrap: 'anywhere',
                  }}
                >
                  {item.publisher}
                </div>
              )}
            </a>
          ))}
        </div>
      </div>
    );
  }

  function renderPublisherMessages(compact = isAdSlotSurface) {
    const messageFontSize = compact ? '13px' : '16.25px';
    return (
      <div
        ref={messagesScrollRef}
        style={{
          flex: compact ? '1 1 0' : 1,
          minHeight: 0,
          maxHeight: '100%',
          overflowY: 'auto',
          overscrollBehavior: 'contain',
          WebkitOverflowScrolling: 'touch',
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
          padding: compact ? '0 2px 4px' : isExpanded ? '16px 16px 8px' : 0,
        }}
      >
        {messages.map((message) => {
          const isActiveSubmittedMessage =
            message.role === 'user' &&
            message.id === activeSubmittedMessageIdRef.current;

          return (
            <div
              key={message.id}
              ref={isActiveSubmittedMessage ? activeSubmittedMessageRef : undefined}
              style={{ marginBottom: '12px', textAlign: message.role === 'user' ? 'right' : 'left' }}
            >
              {(() => {
              const answerHasStarted = Boolean(message.content.trim());
              const shouldShowRecommendations = answerHasStarted || !isLoading;

              return (
                <>
            <div
              style={{
                display: message.role === 'assistant' ? 'block' : 'inline-block',
                width: message.role === 'assistant' ? '100%' : undefined,
                maxWidth: message.role === 'assistant' ? '100%' : '76%',
                padding: message.role === 'assistant' ? '4px 6px 4px 2px' : '10px 14px',
                borderRadius: message.role === 'assistant' ? 0 : '18px',
                background: message.role === 'user' ? publisherAccent : 'transparent',
                color: message.role === 'user' ? sendButtonTextColor : brandText,
                boxShadow: message.role === 'user' ? '0 10px 24px rgba(15, 23, 42, 0.12)' : 'none',
                border: 'none',
                fontSize: message.role === 'assistant' ? messageFontSize : compact ? '12px' : '13px',
                lineHeight: message.role === 'assistant' ? 1.62 : 1.4,
              }}
            >
              {message.role === 'assistant' ? (
                message.content.trim() ? (
                  <CustomMarkdownRenderer content={message.content} theme={{ ...theme, textColor: brandText }} fontSize={messageFontSize} />
                ) : isLoading && message.id === activeAssistantMessageIdRef.current ? (
                  renderGeneratingIndicator({ includeSteps: true })
                ) : null
              ) : (
                <p style={{ margin: 0, color: sendButtonTextColor }}>{message.content}</p>
              )}
              {message.role === 'assistant' && renderSourceLinks(message)}
              {message.role === 'assistant' && renderRelatedItems(message, compact)}
              {message.role === 'assistant' && renderAnswerFooter(message)}
              {message.role === 'assistant' && shouldShowRecommendations && sponsoredClickedMessages.has(message.id) && message.initialRecommendation && (
                <div style={{ marginTop: '14px' }}>
                  <AdMeshRecommendations
                    messageId={message.id}
                    query={message.userQuery}
                    initialRecommendation={message.initialRecommendation}
                    onFollowupDetected={upsertSponsoredFollowupSuggestion}
                    onRecommendationsShown={(messageId, recommendation) => {
                      handleRecommendationShown(messageId, recommendation, message.userQuery);
                    }}
                    onError={(recommendationError) => {
                      console.error('[AdMeshIntentAssistant] Recommendation error:', recommendationError);
                    }}
                  />
                </div>
              )}
              {message.role === 'assistant' && shouldShowRecommendations && !sponsoredClickedMessages.has(message.id) && (message.initialRecommendation || canUseDirectAdMeshFetch) && (
                <div style={{ marginTop: '14px' }}>
                  <AdMeshRecommendations
                    messageId={message.id}
                    query={message.userQuery}
                    initialRecommendation={message.initialRecommendation || undefined}
                    onFollowupDetected={upsertSponsoredFollowupSuggestion}
                    onRecommendationsShown={(messageId, recommendation) => {
                      handleRecommendationShown(messageId, recommendation, message.userQuery);
                    }}
                    onError={(recommendationError) => {
                      console.error('[AdMeshIntentAssistant] Recommendation error:', recommendationError);
                    }}
                  />
                </div>
              )}
            </div>
                </>
              );
            })()}
            </div>
          );
        })}

        {error && (
          <div style={{ color: publisherMeta, fontSize: fontSizes.sm }}>Connection issue. Please try again.</div>
        )}

        <div ref={messagesEndRef} />
      </div>
    );
  }

  if (variant === 'default') {
    const expandedOverlayBackdropStyle: CSSProperties = {
      position: 'fixed',
      inset: 0,
      background: expandedOverlay,
      backdropFilter: 'blur(6px)',
      zIndex: 2147483000,
    };

    function renderExpandedOverlayPortal(panel: ReactNode) {
      const overlayLayout = getExpandedOverlayLayout(isMobileViewport);
      return createPortal(
        <>
          <div style={expandedOverlayBackdropStyle} />
          <div style={overlayLayout.shell}>{panel}</div>
        </>,
        document.body,
      );
    }

    function renderTeaser(forOverlay = false) {
      const compact = isAdSlotCompactLayout(forOverlay);
      const panelExpanded = forOverlay ? true : isExpanded;
      const ultraCompact = compact && !panelExpanded && isUltraCompactAdSlot;
      const showMessagesInTeaser =
        panelExpanded && !(isAdSlotSurface && adSlotBehavior === 'expand' && !forOverlay && isAdSlotOverlayActive);
      const expandedOverlayLayout = panelExpanded ? getExpandedOverlayLayout(isMobileViewport) : null;
      const mobileExpandedOverlay = Boolean(expandedOverlayLayout && isMobileViewport && !compact);
      const teaserPanelWidth = compact
        ? adSlotWidthCss
        : expandedOverlayLayout
          ? expandedOverlayLayout.panelWidth
          : layout === 'inline'
            ? '100%'
            : 'min(420px, calc(100vw - 32px))';
      const teaserPanelHeight = compact
        ? adSlotHeightCss
        : expandedOverlayLayout
          ? expandedOverlayLayout.panelHeight
          : 'auto';
      const teaserPanelMaxWidth = compact ? adSlotWidthCss : expandedOverlayLayout?.panelMaxWidth ?? '100%';
      const teaserPanelMaxHeight = compact ? adSlotHeightCss : expandedOverlayLayout?.panelMaxHeight;
      const showTeaserHeader =
        showHeader && !ultraCompact && (panelExpanded || compact) && (forOverlay || panelExpanded || showCompactSlotHeader);

      return (
      <div
        style={{
          width: teaserPanelWidth,
          maxWidth: teaserPanelMaxWidth,
          height: teaserPanelHeight,
          maxHeight: teaserPanelMaxHeight,
          boxSizing: 'border-box',
          display: 'flex',
          flexDirection: 'column',
          background: panelExpanded ? expandedShellBackground : publisherSurfaceBg,
          borderRadius: compact
            ? '12px'
            : expandedOverlayLayout
              ? expandedOverlayLayout.panelBorderRadius
              : panelExpanded
                ? '18px'
                : '20px',
          padding: panelExpanded ? 0 : compact ? '8px' : '12px',
          boxShadow: mobileExpandedOverlay ? 'none' : panelExpanded ? expandedShadow : 'none',
          border: mobileExpandedOverlay ? 'none' : panelExpanded ? `1px solid ${expandedBorderStrong}` : 'none',
          overflow: 'hidden',
          minHeight: compact ? 0 : undefined,
        }}
      >
        {showTeaserHeader && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: compact ? '10px 12px' : '16px 18px',
              background: panelExpanded ? expandedHeaderBackground : '#ffffff29',
              color: brandText,
              borderBottom: `1px solid ${expandedBorder}`,
              flexShrink: 0,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div
                style={{
                  width: compact ? 30 : 40,
                  height: compact ? 30 : 40,
                  borderRadius: compact ? '10px' : '14px',
                  background: withAlpha(brandPrimary, 0.08),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 16,
                  fontWeight: 700,
                  color: publisherAccent,
                  overflow: 'hidden',
                }}
              >
                {headerLogoUrl ? (
                  <img src={headerLogoUrl} alt={`${headerTitle || 'Assistant'} logo`} style={{ width: '100%', height: '100%', objectFit: 'contain', background: '#ffffff', padding: 4 }} />
                ) : (
                  (headerTitle || 'AA').slice(0, 2).toUpperCase()
                )}
              </div>
              {(showHeaderTitle || showSubtitle) && (
                <div>
                  {showHeaderTitle && <div style={{ fontSize: compact ? '14px' : '20px', fontWeight: 700, lineHeight: 1.1, letterSpacing: 0 }}>{headerTitle}</div>}
                  {showSubtitle && <div style={{ fontSize: compact ? '10px' : '12px', color: expandedMutedText, marginTop: '2px' }}>{assistantSubtitle}</div>}
                </div>
              )}
            </div>
            {panelExpanded && (!compact || forOverlay) && (
              <button
                type="button"
                onClick={() => setIsExpanded(false)}
                aria-label="Minimize assistant"
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: '12px',
                  border: `1px solid ${expandedBorder}`,
                  background: '#ffffff',
                  color: expandedMutedText,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <span style={{ fontSize: '18px', lineHeight: 1, fontWeight: 700, transform: 'translateY(-1px)' }}>-</span>
              </button>
            )}
          </div>
        )}
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: ultraCompact ? '2px' : compact ? '6px' : '16px',
            minHeight: 0,
            flex: compact ? '1 1 0' : undefined,
            overflow: compact ? 'hidden' : undefined,
            overscrollBehavior: 'contain',
            padding: ultraCompact ? '3px 6px 2px' : compact ? '8px 10px 6px' : panelExpanded ? '18px' : '14px',
            background: panelExpanded ? expandedShellBackground : withAlpha('#ffffff', 0.16),
            height: compact ? undefined : panelExpanded ? teaserPanelHeight : 'auto',
          }}
        >
          {renderDelegationBanner('default')}
          {showMessagesInTeaser && renderPublisherMessages(compact)}
          {compact && !panelExpanded && !ultraCompact && <div aria-hidden="true" style={{ flex: '1 1 auto', minHeight: 0 }} />}
          {compact && !panelExpanded && showWelcomeMessage && showCompactWelcomeMessage && messages.length === 0 && (
            <div
              style={{
                color: brandText,
                fontSize: '15px',
                lineHeight: 1.3,
                fontWeight: 700,
                letterSpacing: 0,
                padding: '2px 2px 0',
                textAlign: 'center',
              }}
            >
              {welcomeMessage}
            </div>
          )}
          {renderPublisherSuggestions(compact)}
          {(!compact || panelExpanded || showCompactSlotInput) && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              borderRadius: compact ? '12px' : '16px',
              background: expandedComposerBackground,
              padding: compact ? '6px 6px 6px 10px' : '8px 8px 8px 14px',
              boxShadow: panelExpanded ? expandedComposerShadow : `0 8px 22px ${purpleShadowSoft}`,
              border: `1px solid ${panelExpanded ? expandedBorder : purpleBorderSoft}`,
              flexShrink: 0,
            }}
          >
            <input
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  if (isLoading) return;
                  currentSponsoredSuggestionRef.current = null;
                  currentMessageIsSponsored.current = false;
                  setIsExpanded(true);
                  if (layout === 'floating') handleOpenChange(true);
                  handleSendMessage(inputValue);
                }
              }}
              placeholder={placeholder}
              style={{
                flex: 1,
                border: 'none',
                outline: 'none',
                borderRadius: '12px',
                padding: compact ? '8px 2px' : '10px 4px',
                color: brandText,
                fontSize: compact ? '13px' : '15px',
                background: 'transparent',
              }}
            />
            <button
              type="button"
              onClick={() => {
                if (isLoading) {
                  handleStopGeneration();
                  return;
                }
                currentSponsoredSuggestionRef.current = null;
                currentMessageIsSponsored.current = false;
                setIsExpanded(true);
                if (layout === 'floating') handleOpenChange(true);
                handleSendMessage(inputValue);
              }}
              disabled={!isLoading && !inputValue.trim()}
              style={{
                border: 'none',
                borderRadius: '12px',
                background: isLoading ? '#dc2626' : publisherAccent,
                color: isLoading ? '#ffffff' : sendButtonTextColor,
                minWidth: compact ? '40px' : '48px',
                height: compact ? '36px' : '44px',
                padding: compact ? '0 10px' : '0 16px',
                fontSize: compact ? '12px' : '14px',
                fontWeight: 700,
                cursor: !isLoading && !inputValue.trim() ? 'default' : 'pointer',
                opacity: !isLoading && !inputValue.trim() ? 0.6 : 1,
                boxShadow: `0 10px 20px ${isLoading ? 'rgba(220, 38, 38, 0.18)' : withAlpha(brandPrimary, 0.18)}`,
              }}
            >
              {isLoading ? 'Stop' : 'Send'}
            </button>
          </div>
          )}
          {panelExpanded && renderExpandedBranding('default', compact)}
          {!panelExpanded && isMobileLeaderboardAdSlot ? (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '8px',
                minHeight: 22,
                paddingTop: 0,
                lineHeight: 1,
                flexShrink: 0,
              }}
            >
              <div style={{ color: publisherMeta, fontSize: '8px', fontWeight: 600, whiteSpace: 'nowrap' }}>
                Powered by{' '}
                <a
                  href="https://useadmesh.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: 'inherit', textDecoration: 'none', fontWeight: 700 }}
                >
                  AdMesh
                </a>
              </div>
              <button
                type="button"
                onClick={() => {
                  setIsExpanded(true);
                  if (layout === 'floating') {
                    handleOpenChange(true);
                  }
                }}
                style={{
                  border: 'none',
                  borderRadius: 999,
                  background: publisherAccent,
                  color: sendButtonTextColor,
                  minWidth: 42,
                  height: 22,
                  padding: '0 10px',
                  fontSize: '10px',
                  fontWeight: 700,
                  lineHeight: 1,
                  cursor: 'pointer',
                  boxShadow: `0 6px 14px ${withAlpha(brandPrimary, 0.16)}`,
                  flexShrink: 0,
                }}
              >
                Ask
              </button>
            </div>
          ) : !panelExpanded && (
            <div style={{ textAlign: 'center', paddingTop: ultraCompact ? 0 : '2px', lineHeight: ultraCompact ? 1 : undefined }}>
              <div style={{ color: publisherMeta, fontSize: ultraCompact ? '8px' : compact ? '9.5px' : fontSizes.xs, fontWeight: 600 }}>
                Powered by{' '}
                <a
                  href="https://useadmesh.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: 'inherit', textDecoration: 'none', fontWeight: 700 }}
                >
                  AdMesh
                </a>
              </div>
              {showCompactSlotDisclaimer && (
              <div style={{ color: publisherMeta, fontSize: compact ? '9px' : fontSizes.xs, lineHeight: compact ? 1.25 : 1.5 }}>{genericDisclaimer}</div>
              )}
            </div>
          )}
        </div>
      </div>
    );
    }

    if (layout === 'inline') {
      if (isAdSlotSurface) {
        return (
          <div
            ref={adSlotRootRef}
            className="admesh-component"
            data-admesh-theme={themeMode}
            data-admesh-surface="ad-slot"
            data-admesh-ad-slot-behavior={adSlotBehavior}
            data-admesh-ad-slot-expanded={shouldExpandAdSlot ? 'true' : 'false'}
            style={{
              width: adSlotWidthCss,
              height: adSlotHeightCss,
              maxWidth: adSlotWidthCss,
              maxHeight: adSlotHeightCss,
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            {renderTeaser(false)}
            {isAdSlotOverlayActive &&
              typeof document !== 'undefined' &&
              renderExpandedOverlayPortal(renderTeaser(true))}
          </div>
        );
      }

      return (
        <div className="admesh-component" data-admesh-theme={themeMode} style={{ width: '100%', position: 'relative' }}>
          {renderTeaser(false)}
          {isExpanded &&
            typeof document !== 'undefined' &&
            renderExpandedOverlayPortal(renderTeaser(true))}
        </div>
      );
    }

    if (!isOpen) {
      return (
        <div className="admesh-component" data-admesh-theme={themeMode}>
          <button
            type="button"
            onClick={handleOpenClick}
            style={{
              position: 'fixed',
              ...positionStyle,
              zIndex: 2147483000,
              border: 'none',
              borderRadius: 999,
              background: theme.primaryColor,
              color: '#ffffff',
              padding: '14px 18px',
              boxShadow: '0 18px 40px rgba(15, 23, 42, 0.18)',
              cursor: 'pointer',
            }}
          >
            {triggerLabel}
          </button>
        </div>
      );
    }

    return (
      <div className="admesh-component" data-admesh-theme={themeMode}>
        {!isExpanded && (
          <div
            style={{
              position: 'fixed',
              ...positionStyle,
              zIndex: 2147483001,
            }}
          >
            {renderTeaser(false)}
          </div>
        )}
        {isExpanded &&
          typeof document !== 'undefined' &&
          renderExpandedOverlayPortal(renderTeaser(true))}
      </div>
    );
  }

  // Conditional rendering: only show if we have suggestions
  if (orderedSuggestions.length === 0 && !shouldShowSuggestionSkeletons) {
    console.log('[AdMeshIntentAssistant] No suggestions available - not rendering');
    return null;
  }

  if (layout === 'inline') {
    return (
      <div className="admesh-component" data-admesh-theme={themeMode}>
        <div
          className={`overflow-hidden rounded-[2rem] ${className || ''}`}
          style={{
            width: toCssSize(panelWidth) ?? '100%',
            backgroundColor: '#eef2ff',
            fontSize: fontSizes.base,
            ...style,
          }}
        >
          <div
            className="space-y-4 rounded-[1.75rem] p-4"
            style={{ backgroundColor: 'rgba(255, 255, 255, 0.35)' }}
          >
            {shouldShowSuggestionSkeletons ? (
              renderSuggestionSkeletons('legacy')
            ) : orderedSuggestions.length > 0 && (
              <div className="flex flex-col gap-3">
                {orderedSuggestions.slice(0, maxSuggestions).map((suggestion, index) => (
                  <button
                    key={suggestion.id}
                    ref={setSuggestionButtonRef(suggestion.id)}
                    onClick={() => handleSuggestionClick(suggestion)}
                    className="rounded-full px-5 py-4 text-left shadow-sm transition-transform duration-200 hover:scale-[1.01] active:scale-[0.99]"
                    style={{
                      width: index === 0 ? '100%' : index === 1 ? '82%' : '68%',
                      backgroundColor: theme.surfaceColor || '#ffffff',
                      color: theme.textColor || '#1e293b',
                      border: `1px solid ${theme.borderColor || '#e2e8f0'}`,
                      fontSize: fontSizes.sm,
                    }}
                  >
                    <span className="leading-tight">{suggestion.text}</span>
                    {adsEnabled && suggestion.is_sponsored && (
                      <sup
                        className="ml-1 rounded-full px-1.5 py-0.5"
                        style={{
                          backgroundColor: theme.backgroundColor || '#ffffff',
                          color: theme.textSecondaryColor || '#64748b',
                          fontSize: '9px',
                          lineHeight: 1,
                        }}
                      >
                        Ad
                      </sup>
                    )}
                  </button>
                ))}
              </div>
            )}

            {messages.length > 0 && (
              <div
                ref={messagesScrollRef}
                className="max-h-[280px] space-y-3 overflow-y-auto rounded-[1.5rem] bg-white/80 p-4"
                style={{ color: theme.textColor, overscrollBehavior: 'contain', WebkitOverflowScrolling: 'touch' }}
              >
                {messages.map((message) => {
                  const isActiveSubmittedMessage =
                    message.role === 'user' &&
                    message.id === activeSubmittedMessageIdRef.current;

                  return (
                    <div
                      key={message.id}
                      ref={isActiveSubmittedMessage ? activeSubmittedMessageRef : undefined}
                      className={message.role === 'user' ? 'text-right' : 'text-left'}
                    >
                    <div
                      className="inline-block max-w-[85%] rounded-2xl px-4 py-3"
                      style={{
                        backgroundColor: message.role === 'user' ? theme.primaryColor : '#ffffff',
                        color: message.role === 'user' ? (theme.primaryTextColor || '#ffffff') : theme.textColor,
                        fontSize: fontSizes.sm,
                      }}
                    >
                      {message.role === 'assistant' ? (
                        message.content.trim() ? (
                          <>
                            <CustomMarkdownRenderer content={message.content} theme={theme} fontSize={fontSizes.sm} />
                            {renderAnswerFooter(message, {
                              accentColor: theme.primaryColor,
                              metaColor: theme.textSecondaryColor,
                            })}
                          </>
                        ) : isLoading && message.id === activeAssistantMessageIdRef.current ? (
                          renderGeneratingIndicator({
                            compact: true,
                            accentColor: theme.primaryColor,
                            metaColor: theme.textSecondaryColor,
                            includeSteps: false,
                          })
                        ) : null
                      ) : (
                        <p style={{ margin: 0, lineHeight: '1.4' }}>{message.content}</p>
                      )}
                    </div>
                  </div>
                  );
                })}

                {isLoading && !messages.some(
                  (message) =>
                    message.role === 'assistant' &&
                    message.id === activeAssistantMessageIdRef.current &&
                    !message.content.trim(),
                ) && (
                  <div className="text-sm" style={{ color: theme.textSecondaryColor }}>
                    {renderGeneratingIndicator({
                      compact: true,
                      accentColor: theme.primaryColor,
                      metaColor: theme.textSecondaryColor,
                      includeSteps: false,
                    })}
                  </div>
                )}

                {error && (
                  <div className="text-sm" style={{ color: theme.textSecondaryColor }}>
                    Connection issue. Please try again.
                  </div>
                )}

                <div ref={messagesEndRef} />
              </div>
            )}

            <div className="rounded-[1.5rem] bg-white p-3 shadow-sm">
              <div className="flex items-center gap-3">
                <input
                  type="text"
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key !== 'Enter') return;
                    if (isLoading) {
                      e.preventDefault();
                      return;
                    }
                    handleSendMessage(inputValue);
                  }}
                  placeholder={placeholder}
                  className="min-w-0 flex-1 rounded-full px-4 py-3 focus:outline-none"
                  style={{
                    backgroundColor: theme.backgroundColor || '#ffffff',
                    color: theme.textColor,
                    fontSize: fontSizes.sm,
                  }}
                />
                <button
                  onClick={() => {
                    if (isLoading) {
                      handleStopGeneration();
                      return;
                    }
                    handleSendMessage(inputValue);
                  }}
                  disabled={!isLoading && !inputValue.trim()}
                  className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full transition-colors disabled:cursor-not-allowed disabled:opacity-50"
                  style={{
                    backgroundColor: isLoading ? '#dc2626' : theme.primaryColor,
                    color: '#ffffff',
                  }}
                >
                  {isLoading ? <XIcon size={18} /> : <SendIcon size={18} />}
                </button>
              </div>
            </div>

            <div className="space-y-2 text-center">
              <p style={{ color: theme.textSecondaryColor, fontSize: fontSizes.xs, fontWeight: 600 }}>
                Powered by{' '}
                <a
                  href="https://useadmesh.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: 'inherit', textDecoration: 'none', fontWeight: 700 }}
                >
                  AdMesh
                </a>
              </p>
              <p style={{ color: theme.textSecondaryColor, fontSize: fontSizes.xs, lineHeight: 1.5 }}>
                {genericDisclaimer}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!isOpen) {
    // Floating button when closed
    return (
      <div className="admesh-component" data-admesh-theme={themeMode}>
        <button
          onClick={handleOpenClick}
          className="flex items-center gap-2 px-4 py-3 rounded-full shadow-lg transition-all duration-200 hover:scale-105 active:scale-95"
          style={{
            position: 'fixed',
            ...positionStyle,
            backgroundColor: theme.primaryColor,
            color: '#ffffff',
            zIndex: 9999,
            fontSize: fontSizes.base,
            ...style,
          }}
        >
          {/* {triggerIcon ?? <BotIcon size={20} />} */}
          <span className="font-medium">{triggerLabel}</span>
        </button>
      </div>
    );
  }

  // Main assistant interface when open
  return (
    <div className="admesh-component" data-admesh-theme={themeMode}>
      <div
        className={`flex flex-col overflow-hidden ${className || ''}`}
        style={{
          position: 'fixed',
          display: 'flex',
          flexDirection: 'column',
          ...(isExpanded ? {
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 'min(1040px, calc(100vw - 40px))',
            height: 'min(84vh, 820px)',
            maxWidth: '1040px',
            maxHeight: '820px',
          } : {
            ...positionStyle,
            width: toCssSize(panelWidth) ?? sizeStyle.width,
            height: toCssSize(panelHeight) ?? sizeStyle.height,
          }),
          backgroundColor: isExpanded ? expandedShellBackground : theme.backgroundColor,
          zIndex: 9999,
          fontSize: fontSizes.base,
          borderRadius: isExpanded ? '20px' : '16px',
          border: isExpanded ? `1px solid ${expandedBorderStrong}` : '1px solid transparent',
          boxShadow: isExpanded ? expandedShadow : '0 20px 40px rgba(15, 23, 42, 0.12)',
          ...style,
        }}
      >
        {/* Header */}
        {showHeader && (
        <div
          className="sticky top-0 z-10 flex items-center justify-between p-4 border-b"
          style={{ 
            position: 'sticky',
            top: 0,
            zIndex: 10,
            borderColor: isExpanded ? expandedBorder : 'transparent',
            background: isExpanded ? expandedHeaderBackground : theme.backgroundColor,
            padding: isExpanded ? '18px 20px' : '16px',
          }}
        >
          <div className="flex items-center gap-3">
            <div
              style={{
                width: isExpanded ? 40 : 32,
                height: isExpanded ? 40 : 32,
                borderRadius: isExpanded ? '14px' : '10px',
                background: withAlpha(brandPrimary, 0.08),
                color: publisherAccent,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: isExpanded ? '15px' : '13px',
                fontWeight: 700,
                flexShrink: 0,
                overflow: 'hidden',
              }}
            >
              {headerLogoUrl ? (
                <img src={headerLogoUrl} alt={`${headerTitle || 'Assistant'} logo`} style={{ width: '100%', height: '100%', objectFit: 'contain', background: '#ffffff', padding: 4 }} />
              ) : (
                (headerTitle || 'AA').slice(0, 2).toUpperCase()
              )}
            </div>
            {(showHeaderTitle || (showSubtitle && isExpanded)) && (
              <div>
                {showHeaderTitle && (
                  <div
                    className="font-medium"
                    style={{ color: theme.textColor, fontSize: isExpanded ? '20px' : fontSizes.base, fontWeight: 700, letterSpacing: isExpanded ? '-0.02em' : 0 }}
                  >
                    {headerTitle}
                  </div>
                )}
                {showSubtitle && isExpanded && (
                  <div style={{ color: expandedMutedText, fontSize: '12px', marginTop: '2px' }}>
                    {assistantSubtitle}
                  </div>
                )}
              </div>
            )}
          </div>
          <div className="flex items-center gap-1">
            {!isAdSlotSurface && (
              <button
                onClick={handleToggleExpand}
                className="transition-colors"
                title={isExpanded ? "Minimize" : "Expand"}
                style={{
                  width: isExpanded ? 36 : 28,
                  height: isExpanded ? 36 : 28,
                  borderRadius: isExpanded ? '12px' : '10px',
                  border: `1px solid ${isExpanded ? expandedBorder : 'transparent'}`,
                  background: isExpanded ? '#ffffff' : 'transparent',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {isExpanded ? (
                  <Minimize2 size={16} style={{ color: theme.textSecondaryColor }} />
                ) : (
                  <Maximize2 size={16} style={{ color: theme.textSecondaryColor }} />
                )}
              </button>
            )}
            <button
              onClick={handleCloseClick}
              className="transition-colors"
              style={{
                width: isExpanded ? 36 : 28,
                height: isExpanded ? 36 : 28,
                borderRadius: isExpanded ? '12px' : '10px',
                border: `1px solid ${isExpanded ? expandedBorder : 'transparent'}`,
                background: isExpanded ? '#ffffff' : 'transparent',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <XIcon size={16} style={{ color: theme.textSecondaryColor }} />
            </button>
          </div>
        </div>
        )}

        {/* Messages */}
        <div
          ref={messagesScrollRef}
          className="flex-1 min-h-0 overflow-y-auto p-4"
          style={{
            flex: '1 1 0%',
            minHeight: 0,
            overflowY: 'auto',
            overscrollBehavior: 'contain',
            WebkitOverflowScrolling: 'touch',
            padding: isExpanded ? '20px' : '16px',
          }}
        >
          {renderDelegationBanner('legacy')}
          {showWelcomeMessage && messages.length === 0 && (
            <div className="mb-3">
              <div
                className="inline-block p-2 rounded-lg"
               
              >
                <p style={{ color: theme.textColor, margin: 0, fontSize: fontSizes.sm }}>{welcomeMessage}</p>
              </div>
            </div>
          )}

          {messages.map((message) => {
            const isActiveSubmittedMessage =
              message.role === 'user' &&
              message.id === activeSubmittedMessageIdRef.current;

            return (
              <div
                key={message.id}
                ref={isActiveSubmittedMessage ? activeSubmittedMessageRef : undefined}
                className="mb-3"
              >
              <div
                className={message.role === 'user' ? 'inline-block max-w-[85%] ml-auto' : 'max-w-[92%]'}
                style={{
                  backgroundColor: message.role === 'user' ? expandedUserBubble : 'transparent',
                  color: message.role === 'user'
                    ? (theme.primaryTextColor || '#ffffff')
                    : theme.textColor,
                  border: 'none',
                  borderRadius: message.role === 'user' ? (isExpanded ? '14px' : '12px') : 0,
                  padding: message.role === 'user' ? (isExpanded ? '12px 14px' : undefined) : '2px 6px 2px 2px',
                  boxShadow: 'none',
                }}
              >
                {message.role === 'assistant' ? (
                  message.content.trim() ? (
                    <>
                      <div style={{ fontSize: `calc(${fontSizes.sm} * 1.3)`, lineHeight: 1.65 }}>
                        <CustomMarkdownRenderer 
                          content={message.content}
                          theme={theme}
                          fontSize={`calc(${fontSizes.sm} * 1.3)`}
                        />
                      </div>
                      {renderAnswerFooter(message, {
                        accentColor: theme.primaryColor,
                        metaColor: theme.textSecondaryColor,
                      })}
                    </>
                  ) : isLoading && message.id === activeAssistantMessageIdRef.current ? (
                    renderGeneratingIndicator({
                      compact: true,
                      accentColor: theme.primaryColor,
                      metaColor: theme.textSecondaryColor,
                      includeSteps: false,
                    })
                  ) : null
                ) : (
                  <p style={{ margin: 0, fontSize: fontSizes.sm, lineHeight: '1.4' }}>{message.content}</p>
                )}
              </div>
              
              {/* Add recommendations after assistant messages */}
              {message.role === 'assistant' && !sponsoredClickedMessages.has(message.id) && (
                  <AdMeshRecommendations
                    messageId={message.id}
                    query={message.userQuery}
                    onFollowupDetected={upsertSponsoredFollowupSuggestion}
                    onRecommendationsShown={(messageId, recommendation) => {
                      handleRecommendationShown(messageId, recommendation, message.userQuery);
                    }}
                  onError={(error) => {
                    console.error(`[AdMeshIntentAssistant] Recommendations error:`, error);
                  }}
                />
              )}
              
              {/* Display sponsored recommendation after assistant content if available */}
              {message.role === 'assistant' && sponsoredClickedMessages.has(message.id) && message.initialRecommendation && !isLoading && (
                <div className="mb-4">
                  <div className="text-xs text-gray-500 mb-2 font-semibold">Sponsored Recommendation</div>
                  <AdMeshRecommendations
                    messageId={message.id}
                    query={message.userQuery}
                    initialRecommendation={message.initialRecommendation}
                    onFollowupDetected={upsertSponsoredFollowupSuggestion}
                    onRecommendationsShown={(messageId, recommendation) => {
                      handleRecommendationShown(messageId, recommendation, message.userQuery);
                    }}
                    onError={(error) => {
                      console.error(`[AdMeshIntentAssistant] Recommendations error:`, error);
                    }}
                  />
                </div>
              )}
            </div>
            );
          })}

          {isLoading && !messages.some(
            (message) =>
              message.role === 'assistant' &&
              message.id === activeAssistantMessageIdRef.current &&
              !message.content.trim(),
          ) && (
            <div className="mb-3">
              <div
                className="inline-block p-2 rounded-lg"
                style={{ backgroundColor: 'transparent' }}
              >
                {renderGeneratingIndicator({
                  compact: true,
                  accentColor: theme.primaryColor,
                  metaColor: theme.textSecondaryColor,
                  includeSteps: false,
                })}
              </div>
            </div>
          )}

          {error && (
          <div className="mb-3">
            <div
              className="inline-block p-2 rounded-lg border"
              style={{ 
                backgroundColor: 'transparent',
                borderColor: 'transparent',
                color: theme.textSecondaryColor
              }}
            >
              <div className="flex items-center gap-2">
                <span style={{ fontSize: fontSizes.xs }}>⚠️</span>
                  <span style={{ fontSize: fontSizes.sm }}>Connection issue. Please try again.</span>
                </div>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

       

        {/* Suggestions */}
        {messages.length === 0 && shouldShowSuggestionSkeletons && (
          <div className="px-4 pb-2">
            {renderSuggestionSkeletons('legacy')}
          </div>
        )}

        {orderedSuggestions.length > 0 && messages.length === 0 && !shouldShowSuggestionSkeletons && (
          <div className="px-4 pb-2">
            <div className="flex flex-col gap-2">
              {orderedSuggestions.slice(0, maxSuggestions).map((suggestion) => (
                  <button
                    key={suggestion.id}
                    ref={setSuggestionButtonRef(suggestion.id)}
                    onClick={() => handleSuggestionClick(suggestion)}
                    className="relative flex w-full items-center text-left transition-all duration-200"
                    style={{
                      backgroundColor: theme.surfaceColor || '#f8fafc',
                      color: theme.textColor || '#1e293b',
                      border: `1px solid ${theme.borderColor || '#e2e8f0'}`,
                      fontSize: fontSizes.sm,
                      padding: '10px 12px',
                      borderRadius: '999px',
                      fontWeight: 500,
                    }}
                  >
                    <span className="leading-tight">{suggestion.text}</span>
                    {adsEnabled && suggestion.is_sponsored && (
                      <sup
                        className="absolute right-1 top-1 rounded-full px-1.5 py-0.5 tracking-wide"
                        style={{
                          fontSize: '9px',
                          lineHeight: 1,
                          backgroundColor: theme.backgroundColor || '#ffffff',
                          color: theme.textSecondaryColor || '#64748b'
                        }}
                      >
                        Ad
                      </sup>
                    )}
                </button>
                ))}
              </div>
          </div>
        )}

        {/* Intent Analysis Error */}
        {intentError && !isAnalyzingIntent && safeSuggestions.length === 0 && (
          <div className="px-4 pb-2">
            <div className="flex flex-col gap-2">
              <div className="p-3 rounded-lg border" style={{ 
                backgroundColor: theme.surfaceColor,
                borderColor: theme.borderColor,
                color: theme.textSecondaryColor
              }}>
                <div className="flex items-center justify-between mb-2">
                  <span style={{ fontSize: fontSizes.sm }}>Intent analysis failed</span>
                  <button
                    onClick={retryIntentAnalysis}
                    className="text-xs px-2 py-1 rounded"
                    style={{ 
                      backgroundColor: theme.primaryColor,
                      color: '#ffffff'
                    }}
                  >
                    Retry
                  </button>
                </div>
                <span style={{ fontSize: fontSizes.xs }}>{intentError}</span>
              </div>
            </div>
          </div>
        )}

        {/* Input */}
        <div className="p-3 border-t" style={{ borderColor: isExpanded ? expandedBorder : 'transparent', padding: isExpanded ? '18px 20px' : undefined, background: isExpanded ? expandedHeaderBackground : undefined }}>
          <div className="flex gap-2" style={{ alignItems: 'center', border: `1px solid ${expandedBorder}`, borderRadius: isExpanded ? '16px' : '12px', background: expandedComposerBackground, boxShadow: isExpanded ? expandedComposerShadow : 'none', padding: isExpanded ? '8px 8px 8px 14px' : undefined }}>
            <input
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyPress={(e) => {
                if (e.key !== 'Enter') return;
                if (isLoading) {
                  e.preventDefault();
                  return;
                }
                handleSendMessage(inputValue);
              }}
              placeholder={placeholder}
              className="flex-1 px-3 py-2 rounded-lg border focus:outline-none focus:ring-2"
              style={{
                backgroundColor: 'transparent',
                borderColor: 'transparent',
                color: theme.textColor,
                fontSize: isExpanded ? '15px' : fontSizes.sm,
                '--tw-ring-color': theme.primaryColor,
              } as CSSProperties}
            />
            <button
              onClick={() => {
                if (isLoading) {
                  handleStopGeneration();
                  return;
                }
                handleSendMessage(inputValue);
              }}
              disabled={!isLoading && !inputValue.trim()}
              className="transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              style={{
                backgroundColor: isLoading ? '#dc2626' : theme.primaryColor,
                color: '#ffffff',
                borderRadius: isExpanded ? '12px' : '10px',
                width: isExpanded ? '46px' : undefined,
                height: isExpanded ? '46px' : undefined,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: isExpanded ? `0 10px 20px ${withAlpha(brandPrimary, 0.18)}` : 'none',
              }}
            >
              {isLoading ? <XIcon size={14} /> : <SendIcon size={14} />}
            </button>
          </div>
          {renderExpandedBranding('legacy')}
        </div>
      </div>
    </div>
  );
};

export default AdMeshIntentAssistant;
